import { useState, useRef, useEffect } from "react";
import React from "react";
import {
  FiUsers, FiFileText, FiPlus, FiTag, FiHash, FiBarChart2,
  FiDollarSign, FiUpload, FiDownload, FiShare2, FiMoon, FiType,
  FiTrash2, FiCreditCard, FiRepeat, FiSliders, FiHelpCircle,
  FiSave, FiInfo, FiCheckSquare,
} from "react-icons/fi";

// ── Fonts ──────────────────────────────────────────────────────────────
const fontLink = document.createElement("link");
fontLink.rel = "stylesheet";
fontLink.href =
  "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&display=swap";
document.head.appendChild(fontLink);

function loadScript(src) {
  return new Promise((res, rej) => {
    if (document.querySelector(`script[src="${src}"]`)) { res(); return; }
    const s = document.createElement("script");
    s.src = src; s.onload = res; s.onerror = rej;
    document.head.appendChild(s);
  });
}

// Preload external libs immediately on module load
loadScript("https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js").catch(() => { });
loadScript("https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js").catch(() => { });
const jsQRLoadPromise = loadScript("https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.min.js");
jsQRLoadPromise.catch(() => { });

// ── Cloud session store (Vercel + MongoDB) ──────────────────────────────
const SESSION_API = "https://splitly-api-psi.vercel.app/api/session";

async function storeSessionCloud(data, key) {
  const url = key ? `${SESSION_API}/${encodeURIComponent(key)}` : SESSION_API;
  const res = await fetch(url, {
    method: key ? "PUT" : "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to store session");
  const json = await res.json();
  return json.key || key;
}

// Matches the shape produced by makeKey() on the backend (base64url, 8 chars)
const SESSION_KEY_PATTERN = /^[A-Za-z0-9_-]{6,12}$/;

async function fetchSessionCloud(key) {
  if (typeof key !== "string" || !SESSION_KEY_PATTERN.test(key)) {
    throw new Error("That doesn't look like a valid session key.");
  }
  const res = await fetch(`${SESSION_API}/${encodeURIComponent(key)}`);
  if (!res.ok) throw new Error("Session not found");
  return res.json();
}

// Decodes a QR code image file to its embedded text using jsQR
function decodeQRImage(file) {
  return new Promise((resolve, reject) => {
    jsQRLoadPromise
      .catch(() => { throw new Error("Could not load the QR scanning library. Check your connection."); })
      .then(() => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const result = window.jsQR
            ? window.jsQR(imageData.data, imageData.width, imageData.height)
            : null;
          URL.revokeObjectURL(img.src);
          if (result?.data) resolve(result.data);
          else reject(new Error("No QR code found in image."));
        };
        img.onerror = () => reject(new Error("Could not read image."));
        img.src = URL.createObjectURL(file);
      })
      .catch(reject);
  });
}

// Scans a live video stream for a QR code, frame by frame, until found or stopped.
// Frames are downscaled and decode attempts throttled, since full-resolution
// getImageData + jsQR on every animation frame is what causes the janky preview.
function scanQRFromVideo(video, canvas, onResult, onError) {
  let stopped = false;
  let lastAttempt = 0;
  const MIN_INTERVAL_MS = 150;
  const MAX_DIM = 480;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  const tick = (t) => {
    if (stopped) return;
    if (video.readyState === video.HAVE_ENOUGH_DATA && window.jsQR && t - lastAttempt >= MIN_INTERVAL_MS) {
      lastAttempt = t;
      const scale = Math.min(1, MAX_DIM / Math.max(video.videoWidth, video.videoHeight));
      canvas.width = Math.round(video.videoWidth * scale);
      canvas.height = Math.round(video.videoHeight * scale);
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const result = window.jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: "dontInvert",
      });
      if (result?.data) { stopped = true; onResult(result.data); return; }
    }
    requestAnimationFrame(tick);
  };
  jsQRLoadPromise.then(() => { if (!stopped) requestAnimationFrame(tick); }).catch(onError);
  return () => { stopped = true; };
}

// ── Currencies ─────────────────────────────────────────────────────────
const CURRENCIES = [
  { code: "PHP", symbol: "₱", name: "Philippine Peso" },
  { code: "USD", symbol: "$", name: "US Dollar" },
  { code: "EUR", symbol: "€", name: "Euro" },
  { code: "GBP", symbol: "£", name: "British Pound" },
  { code: "JPY", symbol: "¥", name: "Japanese Yen" },
  { code: "KRW", symbol: "₩", name: "South Korean Won" },
  { code: "THB", symbol: "฿", name: "Thailand Baht" },
  { code: "SGD", symbol: "S$", name: "Singapore Dollar" },
  { code: "HKD", symbol: "HK$", name: "Hong Kong Dollar" },
  { code: "AUD", symbol: "A$", name: "Australian Dollar" },
  { code: "CAD", symbol: "C$", name: "Canadian Dollar" },
];

// ── Helpers ────────────────────────────────────────────────────────────
const uid = () => Math.random().toString(36).slice(2, 9);
const fmt = (sym, n) =>
  `${sym}${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ── Palette ────────────────────────────────────────────────────────────
const palette = {
  light: {
    bg: "#f5f7f4", surface: "#ffffff", surfaceAlt: "#eef1ec",
    border: "#d4dcd0", borderStrong: "#b0bfaa",
    text: "#1e2b1a", textMuted: "#5a7052",
    accent: "#5a8a5e", accentHover: "#4a7a4e", accentSoft: "#d8ead9", accentText: "#ffffff",
    danger: "#c0392b", dangerSoft: "#fdecea",
    shadow: "0 2px 12px rgba(90,138,94,0.10)", shadowLg: "0 8px 32px rgba(90,138,94,0.13)",
    tag: "#e3ede3", tagText: "#3a6b3f",
  },
  dark: {
    bg: "#141a13", surface: "#1e2b1a", surfaceAlt: "#253021",
    border: "#2e3f29", borderStrong: "#3d5437",
    text: "#dcebd8", textMuted: "#7da87a",
    accent: "#6aaa6e", accentHover: "#7aba7e", accentSoft: "#1e3320", accentText: "#0e1a0d",
    danger: "#e05c4e", dangerSoft: "#2d1512",
    shadow: "0 2px 12px rgba(0,0,0,0.35)", shadowLg: "0 8px 32px rgba(0,0,0,0.45)",
    tag: "#253021", tagText: "#7da87a",
  },
};

// ── Global styles ──────────────────────────────────────────────────────
function injectStyles(c, fontSize = 14) {
  const id = "splitly-styles";
  let el = document.getElementById(id);
  if (!el) { el = document.createElement("style"); el.id = id; document.head.appendChild(el); }
  el.textContent = `
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Space Grotesk', sans-serif; background: ${c.bg}; color: ${c.text}; transition: background 0.4s ease, color 0.4s ease; font-size: ${fontSize}px; }
    input, select, button { font-family: 'Space Grotesk', sans-serif; }
    ::-webkit-scrollbar { width: 6px; }
    ::-webkit-scrollbar-track { background: ${c.bg}; }
    ::-webkit-scrollbar-thumb { background: ${c.borderStrong}; border-radius: 3px; }
    .splitly-input {
      background: ${c.surfaceAlt}; border: 1.5px solid ${c.border}; color: ${c.text};
      border-radius: 10px; padding: 8px 12px; font-size: ${fontSize}px;
      font-family: 'Space Grotesk', sans-serif; outline: none;
      transition: border-color 0.2s, box-shadow 0.2s, background 0.4s ease, color 0.4s ease;
      width: 100%;
    }
    .splitly-input:focus { border-color: ${c.accent}; box-shadow: 0 0 0 3px ${c.accentSoft}; }
    .splitly-input::placeholder { color: ${c.textMuted}; opacity: 0.7; }
    .splitly-select {
      background: ${c.surfaceAlt}; border: 1.5px solid ${c.border}; color: ${c.text};
      border-radius: 10px; padding: 8px 12px; font-size: ${fontSize}px;
      font-family: 'Space Grotesk', sans-serif; outline: none; cursor: pointer;
      transition: border-color 0.2s, background 0.4s ease, color 0.4s ease;
    }
    .splitly-select:focus { border-color: ${c.accent}; }
    .btn-primary {
      background: ${c.accent}; color: ${c.accentText}; border: none;
      border-radius: 10px; padding: 9px 18px; font-size: ${fontSize}px; font-weight: 600; cursor: pointer;
      transition: background 0.2s, transform 0.1s, box-shadow 0.2s, opacity 0.2s;
      font-family: 'Space Grotesk', sans-serif;
    }
    .btn-primary:hover:not(:disabled) { background: ${c.accentHover}; box-shadow: 0 4px 12px ${c.accentSoft}; }
    .btn-primary:active:not(:disabled) { transform: scale(0.97); }
    .btn-primary:disabled { opacity: 0.38; cursor: not-allowed; }
    .btn-ghost {
      background: transparent; color: ${c.textMuted}; border: 1.5px solid ${c.border};
      border-radius: 10px; padding: 8px 14px; font-size: ${Math.max(fontSize - 1, 10)}px; font-weight: 500; cursor: pointer;
      transition: all 0.2s, background 0.4s ease, color 0.4s ease, border-color 0.4s ease;
      font-family: 'Space Grotesk', sans-serif;
    }
    .btn-ghost:hover { border-color: ${c.accent}; color: ${c.accent}; background: ${c.accentSoft}; }
    .btn-danger {
      background: transparent; color: ${c.danger}; border: none;
      padding: 4px 8px; border-radius: 6px; font-size: ${Math.max(fontSize - 1, 10)}px; cursor: pointer;
      transition: background 0.2s; font-family: 'Space Grotesk', sans-serif;
    }
    .btn-danger:hover { background: ${c.dangerSoft}; }
    .card {
      background: ${c.surface}; border: 1.5px solid ${c.border}; border-radius: 16px;
      box-shadow: ${c.shadow}; overflow: hidden;
      transition: background 0.4s ease, border-color 0.4s ease, box-shadow 0.4s ease;
    }
    .tag {
      background: ${c.tag}; color: ${c.tagText}; border-radius: 6px;
      padding: 2px 8px; font-size: ${Math.max(fontSize - 2, 10)}px; font-weight: 600; display: inline-block;
    }
    .chip {
      display: inline-flex; align-items: center; gap: 5px;
      background: ${c.accentSoft}; color: ${c.accent}; border-radius: 20px;
      padding: 3px 10px; font-size: ${Math.max(fontSize - 1, 10)}px; font-weight: 500;
    }
    .divider { border: none; border-top: 1.5px solid ${c.border}; margin: 0; }
    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
    @keyframes fadeSlide { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
    .fade-slide { animation: fadeSlide 0.25s ease both; }
    .tab-panel { animation: fadeSlide 0.22s ease both; }
    .qr-modal-overlay { animation: qrOverlayIn 0.2s ease; }
    .qr-modal-overlay.closing { animation: qrOverlayOut 0.2s ease forwards; }
    .qr-modal-panel { animation: qrPanelIn 0.2s cubic-bezier(0.16, 1, 0.3, 1); }
    .qr-modal-panel.closing { animation: qrPanelOut 0.2s ease forwards; }
    @keyframes qrOverlayIn { from { opacity: 0; } to { opacity: 1; } }
    @keyframes qrOverlayOut { from { opacity: 1; } to { opacity: 0; } }
    @keyframes qrPanelIn {
      from { opacity: 0; transform: scale(0.94) translateY(8px); }
      to   { opacity: 1; transform: scale(1) translateY(0); }
    }
    @keyframes qrPanelOut {
      from { opacity: 1; transform: scale(1) translateY(0); }
      to   { opacity: 0; transform: scale(0.94) translateY(8px); }
    }
    @keyframes splashIconColor {
      from { color: ${c.accentText}; opacity: 0.3; }
      to   { color: #ffffff; opacity: 1; }
    }
    .splash-icon-text { animation: splashIconColor 0.9s ease forwards; }
    .section-header {
      font-size: ${Math.max(fontSize - 3, 9)}px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase;
      color: ${c.textMuted}; margin-bottom: 10px;
    }
    .member-pill {
      display: flex; align-items: center; gap: 8px;
      background: ${c.surfaceAlt}; border: 1.5px solid ${c.border};
      border-radius: 12px; padding: 7px 12px; font-size: ${fontSize}px; font-weight: 500;
      transition: border-color 0.2s, background 0.4s ease;
    }
    .member-pill:hover { border-color: ${c.borderStrong}; }
    .avatar {
      width: 28px; height: 28px; border-radius: 50%; background: ${c.accent};
      color: ${c.accentText}; display: flex; align-items: center; justify-content: center;
      font-size: 12px; font-weight: 700; flex-shrink: 0;
    }
    .summary-row {
      display: flex; justify-content: space-between; align-items: center;
      padding: 10px 16px; font-size: ${fontSize}px; border-bottom: 1px solid ${c.border};
      transition: background 0.15s; gap: 8px; min-width: 0;
    }
    .summary-row:last-child { border-bottom: none; }
    .summary-total { font-size: ${fontSize + 4}px; font-weight: 700; color: ${c.accent}; }
    .toggle-checkbox { position: relative; width: 40px; height: 22px; cursor: pointer; }
    .toggle-checkbox input { opacity: 0; width: 0; height: 0; }
    .toggle-slider {
      position: absolute; inset: 0; background: ${c.border}; border-radius: 11px; transition: background 0.2s;
    }
    .toggle-slider::after {
      content: ''; position: absolute; left: 3px; top: 3px;
      width: 16px; height: 16px; border-radius: 50%; background: white; transition: transform 0.2s;
    }
    .toggle-checkbox input:checked + .toggle-slider { background: ${c.accent}; }
    .toggle-checkbox input:checked + .toggle-slider::after { transform: translateX(18px); }
    .tab-btn {
      padding: 6px 16px; border: none; background: transparent; color: ${c.textMuted};
      font-size: ${fontSize}px; font-weight: 500; cursor: pointer; border-radius: 8px;
      transition: all 0.2s; font-family: 'Space Grotesk', sans-serif;
    }
    .tab-btn.active { background: ${c.accentSoft}; color: ${c.accent}; font-weight: 700; }
    .empty-state { text-align: center; padding: 32px 16px; color: ${c.textMuted}; font-size: ${fontSize}px; }
    .amount-input-row { display: flex; align-items: center; gap: 6px; }
    .amount-prefix { font-size: ${fontSize + 1}px; font-weight: 600; color: ${c.textMuted}; flex-shrink: 0; }
    .highlight-box {
      background: ${c.accentSoft}; border: 1.5px solid ${c.borderStrong};
      border-radius: 12px; padding: 12px 16px; margin-top: 8px;
    }
    .settlement-txn {
      display: flex; flex-wrap: wrap; align-items: center; gap: 8px;
      background: ${c.surfaceAlt}; border: 1.5px solid ${c.border};
      border-radius: 12px; padding: 10px 14px; font-size: ${fontSize}px;
      transition: background 0.4s ease; min-width: 0;
    }
    .payer-group-card {
      background: ${c.surface}; border: 1.5px solid ${c.border};
      border-radius: 16px; overflow: hidden; margin-bottom: 16px;
      box-shadow: ${c.shadow}; transition: background 0.4s ease, border-color 0.4s ease;
    }
  `;
}

// ── Avatar ─────────────────────────────────────────────────────────────
const AVATAR_COLORS = [
  "#5a8a5e", "#3d7a5c", "#6aaa8e", "#4a6e5a", "#7a9a6e",
  "#5a7a4e", "#3d5e48", "#6e8a5a", "#4e6e3a", "#7a8e5a",
];
function avatarColor(name) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + (h << 5) - h;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

// Toggle component — kept for potential future use
// function Toggle({ checked, onChange }) {
//   return (
//     <label className="toggle-checkbox">
//       <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
//       <span className="toggle-slider" />
//     </label>
//   );
// }

function Avatar({ name, size = 28 }) {
  const bg = avatarColor(name);
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", background: bg,
      color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.42, fontWeight: 700, flexShrink: 0, letterSpacing: "-0.02em",
    }}>
      {name.slice(0, 2).toUpperCase()}
    </div>
  );
}

// ── Transaction Item ───────────────────────────────────────────────────
function TransactionItem({ item, members, sym, c, onChange, onRemove }) {
  const allSelected = members.length > 0 && item.splitWith.length === members.length;

  const toggleMember = (id) => {
    const next = item.splitWith.includes(id)
      ? item.splitWith.filter(n => n !== id)
      : [...item.splitWith, id];
    onChange({ ...item, splitWith: next });
  };

  const toggleAll = () => {
    onChange({ ...item, splitWith: allSelected ? [] : members.map(m => m.id) });
  };

  return (
    <div className="fade-slide" style={{
      border: `1.5px solid ${c.border}`, borderRadius: 12, padding: "12px 14px",
      background: c.surfaceAlt, display: "flex", flexDirection: "column", gap: 10,
    }}>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
        <input className="splitly-input" style={{ flex: 2, minWidth: 0 }}
          placeholder="Description (e.g. Pasta)" value={item.description}
          onChange={e => onChange({ ...item, description: e.target.value })} />
        <div className="amount-input-row" style={{ flex: 1, minWidth: 80 }}>
          <span className="amount-prefix">{sym}</span>
          <input className="splitly-input" style={{ minWidth: 0 }} type="number" min="0"
            placeholder="0.00" value={item.amount}
            onChange={e => onChange({ ...item, amount: e.target.value })} />
        </div>
        <button className="btn-danger" onClick={onRemove} title="Remove item">✕</button>
      </div>
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: c.textMuted, marginBottom: 6 }}>
          Split with
        </div>
        {members.length === 0 ? (
          <div style={{ fontSize: 12, color: c.textMuted, fontStyle: "italic" }}>Add members first</div>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            <button onClick={toggleAll} style={{
              display: "flex", alignItems: "center", gap: 5, padding: "4px 11px",
              borderRadius: 20, border: `1.5px solid ${allSelected ? c.accent : c.border}`,
              background: allSelected ? c.accent : c.surfaceAlt,
              color: allSelected ? c.accentText : c.textMuted,
              fontSize: 12, fontWeight: 700, cursor: "pointer",
              fontFamily: "'Space Grotesk', sans-serif", transition: "all 0.15s",
            }}>All</button>
            {members.map(m => {
              const active = item.splitWith.includes(m.id);
              return (
                <button key={m.id} onClick={() => toggleMember(m.id)} style={{
                  display: "flex", alignItems: "center", gap: 6, padding: "4px 10px 4px 5px",
                  borderRadius: 20, border: `1.5px solid ${active ? c.accent : c.border}`,
                  background: active ? c.accentSoft : c.surfaceAlt,
                  color: active ? c.accent : c.textMuted,
                  fontSize: 12, fontWeight: active ? 600 : 400, cursor: "pointer",
                  fontFamily: "'Space Grotesk', sans-serif", transition: "all 0.15s",
                }}>
                  <Avatar name={m.name} size={18} />{m.name}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Confirm Modal ──────────────────────────────────────────────────────
function ConfirmModal({ message, onConfirm, onCancel, c }) {
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center",
      justifyContent: "center", background: "rgba(0,0,0,0.45)",
      backdropFilter: "blur(4px)", animation: "fadeIn 0.15s ease",
    }}>
      <div className="card fade-slide" style={{
        width: "100%", maxWidth: 360, margin: "0 20px", padding: "28px 24px",
        display: "flex", flexDirection: "column", gap: 20, background: c.surface,
      }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: 20 }}><FiTrash2 /></div>
          <div style={{ fontWeight: 700, fontSize: 16, color: c.text }}>Delete group?</div>
          <div style={{ fontSize: 14, color: c.textMuted, lineHeight: 1.5 }}>{message}</div>
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button className="btn-ghost" onClick={onCancel}>Cancel</button>
          <button onClick={onConfirm} style={{
            background: c.danger, color: "#fff", border: "none", borderRadius: 10,
            padding: "9px 18px", fontSize: 14, fontWeight: 600, cursor: "pointer",
            fontFamily: "'Space Grotesk', sans-serif", transition: "opacity 0.15s",
          }}
            onMouseOver={e => e.currentTarget.style.opacity = "0.85"}
            onMouseOut={e => e.currentTarget.style.opacity = "1"}>Delete</button>
        </div>
      </div>
    </div>
  );
}

// ── How To Use Modal ───────────────────────────────────────────────────
function HowToModal({ onClose, c }) {
  const steps = [
    {
      icon: <FiUsers />, title: "Add Members",
      body: "Type a name and hit Add (or press Enter) for everyone splitting the bill. Members can be removed at any time — their share is automatically excluded from all groups.",
    },
    {
      icon: <FiFileText />, title: "Create Groups",
      body: "Each venue or occasion gets its own Group (e.g. \"Samgyup Restaurant\", \"Convenience Store\"). Hit + New Group, give it a name, then add items inside it.",
    },
    {
      icon: <FiPlus />, title: "Add Items",
      body: "Each item has a description and an amount. Use the member chips to choose who shares it — tap All to include everyone, or pick individuals. Items are split equally among selected members.",
    },
    {
      icon: <FiTag />, title: "Discounts",
      body: "Got a voucher or promo? Enter a discount amount on the group, then choose how to split it: Proportional (members who ordered more save more) or Equal (everyone saves the same amount).",
    },
    {
      icon: <FiHash />, title: "Grand Total & Service Charge",
      body: "If the receipt includes a service charge, enter the Grand Total (the final amount on the bill). Splitly back-calculates the SC and splits it proportionally based on each member's share after discounts.",
    },
    {
      icon: <FiBarChart2 />, title: "Summary Tab",
      body: "Shows exactly what each person owes in total, then breaks it down group by group — including any discount savings and SC added per member.",
    },
    {
      icon: <FiDollarSign />, title: "Payer Tab",
      body: "Record who actually put money down for each group. Use Paid all for a single payer, or enter split amounts manually. Splitly then calculates the minimum number of transfers needed to settle everyone up.",
    },
    {
      icon: <FiCheckSquare />, title: "Settled Tab",
      body: "Track repayments for everyone who owes money. Tick Settled for a full payment, or enter the amount paid so far for a partial one. Progress is saved with your session export.",
    },
    {
      icon: <FiUpload />, title: "Share as Image",
      body: "Every tab has a Share as Image button that exports that view as a PNG.",
    },
  ];

  const tips = [
    { icon: <FiMoon />, label: "Dark mode", desc: "Toggle between light and dark themes using the ✦ button in the header." },
    { icon: <FiType />, label: "Text size", desc: "Adjust the font size from the ☰ menu to suit your screen." },
    { icon: <FiShare2 />, label: "Share session", desc: "Export all three tabs — Transactions, Summary, and Settlements — combined into one image." },
    { icon: <FiUpload />, label: "Import session", desc: "Scan a QR code or enter a key to restore a session on this device." },
    { icon: <FiDownload />, label: "Export session", desc: "Generates a QR code and key so you can load your session on another device." },
  ];

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1200, display: "flex", alignItems: "center",
      justifyContent: "center", background: "rgba(0,0,0,0.5)",
      backdropFilter: "blur(6px)", animation: "fadeIn 0.15s ease",
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="card fade-slide" style={{
        width: "100%", maxWidth: 480, margin: "0 20px", background: c.surface,
        display: "flex", flexDirection: "column", maxHeight: "88vh", overflow: "hidden",
      }}>
        <div style={{ padding: "16px 20px", borderBottom: `1.5px solid ${c.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>✦ How to Use Splitly</div>
          <button className="btn-danger" onClick={onClose} style={{ fontSize: 16 }}>✕</button>
        </div>
        <div style={{ overflowY: "auto", padding: "20px", display: "flex", flexDirection: "column", gap: 14 }}>

          {/* ── Core workflow steps ── */}
          {steps.map((s, i) => (
            <div key={i} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10, background: c.accentSoft,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 18, flexShrink: 0,
              }}>{s.icon}</div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 3, color: c.text }}>
                  <span style={{ color: c.textMuted, fontSize: 11, fontWeight: 600, marginRight: 6 }}>STEP {i + 1}</span>
                  {s.title}
                </div>
                <div style={{ fontSize: 13, color: c.textMuted, lineHeight: 1.6 }}>{s.body}</div>
              </div>
            </div>
          ))}

          {/* ── Divider ── */}
          <div style={{ borderTop: `1.5px solid ${c.border}`, margin: "4px 0" }} />

          {/* ── Tips & extras ── */}
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: c.textMuted }}>
            Navigation Bar
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {tips.map((t, i) => (
              <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <div style={{
                  width: 30, height: 30, borderRadius: 8, background: c.surfaceAlt,
                  border: `1.5px solid ${c.border}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 15, flexShrink: 0,
                }}>{t.icon}</div>
                <div style={{ paddingTop: 3 }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: c.text }}>{t.label} </span>
                  <span style={{ fontSize: 13, color: c.textMuted, lineHeight: 1.5 }}>— {t.desc}</span>
                </div>
              </div>
            ))}
          </div>

          {/* ── Stateless note ── */}
          <div style={{ marginTop: 2, padding: "11px 14px", background: c.accentSoft, borderRadius: 12, fontSize: 12, color: c.accent, fontWeight: 600, lineHeight: 1.6, textAlign: "center" }}>
            ✦ Your session lives only in this browser tab — nothing is saved automatically. Export it to save your work, or use QR export to transfer it to another device.
          </div>

        </div>
        <div style={{ padding: "14px 20px", borderTop: `1.5px solid ${c.border}`, display: "flex", justifyContent: "flex-end" }}>
          <button className="btn-primary" onClick={onClose}>Got it!</button>
        </div>
      </div>
    </div>
  );
}

// ── Calculation helpers ────────────────────────────────────────────────
function memberItemShare(item, memberId) {
  if (!item.splitWith.includes(memberId)) return 0;
  const n = item.splitWith.length || 1;
  return (parseFloat(item.amount) || 0) / n;
}
function memberGroupSubtotal(groupItems, memberId) {
  return groupItems.reduce((s, it) => s + memberItemShare(it, memberId), 0);
}
// Returns a member's discount share given the group's discount mode
function memberDiscountShare(group, memberId, subtotal, discount) {
  if (discount === 0) return 0;
  const ms = memberGroupSubtotal(group.items, memberId);
  if (ms === 0) return 0;
  if ((group.discountMode || "proportional") === "equal") {
    // Split equally among all members who have any items in this group
    const activeMembers = group.items
      .flatMap(it => it.splitWith)
      .filter((id, i, arr) => arr.indexOf(id) === i);
    return activeMembers.length > 0 ? discount / activeMembers.length : 0;
  }
  // Default: proportional by subtotal share
  return subtotal > 0 ? (ms / subtotal) * discount : 0;
}

// Returns a member's SC share given the group's scMode
function memberSCShare(group, memberId, discountedSubtotal, sc, discShare) {
  if (sc === 0) return 0;
  const ms = memberGroupSubtotal(group.items, memberId);
  if (ms === 0) return 0;
  const afterDiscount = ms - discShare;
  if ((group.scMode || "proportional") === "equal") {
    const activeMembers = group.items
      .flatMap(it => it.splitWith)
      .filter((id, i, arr) => arr.indexOf(id) === i);
    return activeMembers.length > 0 ? sc / activeMembers.length : 0;
  }
  // Default: proportional by after-discount share
  return discountedSubtotal > 0 ? (afterDiscount / discountedSubtotal) * sc : 0;
}

// Returns a member's net share for a group: subtotal share − discount share + SC share
function memberGroupTotal(group, memberId) {
  const subtotal = group.items.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);
  const grandTotal = parseFloat(group.grandTotal) || 0;
  const discount = Math.min(parseFloat(group.discount) || 0, subtotal);
  const discountedSubtotal = Math.max(0, subtotal - discount);
  const sc = grandTotal > 0 ? Math.max(0, grandTotal - discountedSubtotal) : 0;
  const ms = memberGroupSubtotal(group.items, memberId);
  if (ms === 0) return 0;
  const discShare = memberDiscountShare(group, memberId, subtotal, discount);
  const afterDiscount = ms - discShare;
  const scShare = memberSCShare(group, memberId, discountedSubtotal, sc, discShare);
  return afterDiscount + scShare;
}

// ── Group Card ─────────────────────────────────────────────────────────
function GroupCard({ group, members, sym, c, onChange, onRemove }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const subtotal = group.items.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);
  const grandTotal = parseFloat(group.grandTotal) || 0;
  const discount = Math.min(parseFloat(group.discount) || 0, subtotal);
  const discountedSubtotal = Math.max(0, subtotal - discount);
  const serviceCharge = grandTotal > 0 ? Math.max(0, grandTotal - discountedSubtotal) : 0;

  const addItem = () => {
    onChange({
      ...group,
      items: [...group.items, { id: uid(), description: "", amount: "", splitWith: members.map(m => m.id) }],
    });
  };
  const updateItem = (id, next) => onChange({ ...group, items: group.items.map(it => it.id === id ? next : it) });
  const removeItem = (id) => onChange({ ...group, items: group.items.filter(it => it.id !== id) });

  return (
    <div className="card fade-slide" style={{ marginBottom: 20 }}>
      <div style={{ padding: "14px 18px", display: "flex", gap: 10, alignItems: "center", background: c.surfaceAlt, borderBottom: `1.5px solid ${c.border}` }}>
        <div style={{ flex: 1 }}>
          <input className="splitly-input" style={{ fontWeight: 600, fontSize: 15 }}
            placeholder="Group name (e.g. a mano)" value={group.name}
            onChange={e => onChange({ ...group, name: e.target.value })} />
        </div>
        <button className="btn-danger" onClick={() => setConfirmDelete(true)} title="Remove group" style={{ fontSize: 15 }}><FiTrash2 /></button>
      </div>
      {confirmDelete && (
        <ConfirmModal
          message={`"${group.name || "This group"}" and all its items will be permanently removed.`}
          onConfirm={() => { setConfirmDelete(false); onRemove(); }}
          onCancel={() => setConfirmDelete(false)}
          c={c}
        />
      )}
      <div style={{ padding: "14px 18px", display: "flex", flexDirection: "column", gap: 10 }}>
        <div className="section-header">Items</div>
        {group.items.length === 0 && (
          <div className="empty-state" style={{ padding: "16px 0" }}>No items yet. Add one below.</div>
        )}
        {group.items.map(it => (
          <TransactionItem key={it.id} item={it} members={members} sym={sym} c={c}
            onChange={next => updateItem(it.id, next)} onRemove={() => removeItem(it.id)} />
        ))}
        <button className="btn-ghost" onClick={addItem} style={{ alignSelf: "center" }}>+ Add Item</button>
      </div>
      <hr className="divider" />
      <div style={{ padding: "14px 18px", display: "flex", flexDirection: "column", gap: 10 }}>
        <div className="section-header">Notes</div>
        <textarea className="splitly-input" placeholder="Add a note for this group (optional)"
          value={group.notes || ""} rows={3}
          style={{ resize: "vertical", minHeight: 60, padding: "9px 12px", fontFamily: "'Space Grotesk', sans-serif" }}
          onChange={e => onChange({ ...group, notes: e.target.value })} />
      </div>
      <hr className="divider" />
      <div style={{ padding: "14px 18px", display: "flex", flexDirection: "column", gap: 10 }}>
        <div className="section-header">Group Totals</div>
        {/* Row 1: Subtotal (full width) */}
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <label style={{ fontSize: 12, color: c.textMuted, fontWeight: 600 }}>Subtotal</label>
          <div style={{ background: c.surfaceAlt, border: `1.5px solid ${c.border}`, borderRadius: 10, padding: "9px 12px", fontSize: 14, fontWeight: 600, color: c.textMuted, height: 38, display: "flex", alignItems: "center" }}>
            {fmt(sym, subtotal)}
          </div>
        </div>
        {/* Row 2: Discount + Service Charge (Grand Total input) side by side */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, alignItems: "end" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 12, color: c.textMuted, fontWeight: 600 }}>Discount <span style={{ fontWeight: 400, fontStyle: "italic" }}>(optional)</span></label>
            <div className="amount-input-row" style={{ height: 38 }}>
              <span className="amount-prefix" style={{ color: discount > 0 ? "#e05c4e" : undefined }}>−{sym}</span>
              <input className="splitly-input" type="number" min="0" placeholder="0.00"
                value={group.discount || ""} style={{ height: 38 }}
                onChange={e => onChange({ ...group, discount: e.target.value })} />
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 12, color: c.textMuted, fontWeight: 600 }}>Billed Total <span style={{ fontWeight: 400, fontStyle: "italic" }}>(incl. SC)</span></label>
            <div className="amount-input-row" style={{ height: 38 }}>
              <span className="amount-prefix">{sym}</span>
              <input className="splitly-input" type="number" min="0" placeholder="Leave blank if no SC"
                value={group.grandTotal} style={{ height: 38 }}
                onChange={e => onChange({ ...group, grandTotal: e.target.value })} />
            </div>
          </div>
        </div>
        {/* Row 3: Split discount toggle — shown when discount is set */}
        {discount > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 12, color: c.textMuted, fontWeight: 600, whiteSpace: "nowrap", minWidth: 90 }}>Split discount:</span>
            <div style={{ display: "flex", gap: 4, flex: 1 }}>
              {["proportional", "equal"].map(mode => {
                const active = (group.discountMode || "proportional") === mode;
                return (
                  <button key={mode} onClick={() => onChange({ ...group, discountMode: mode })} style={{
                    flex: 1, padding: "5px 0", borderRadius: 8, border: `1.5px solid ${active ? c.danger : c.border}`,
                    background: active ? c.dangerSoft : c.surfaceAlt,
                    color: active ? c.danger : c.textMuted,
                    fontSize: 11, fontWeight: active ? 700 : 500, cursor: "pointer",
                    fontFamily: "'Space Grotesk', sans-serif", transition: "all 0.15s",
                  }}>
                    {mode === "proportional" ? "Proportional" : "Equal"}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {/* Row 4: Split SC toggle — shown when SC is set */}
        {serviceCharge > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 12, color: c.textMuted, fontWeight: 600, whiteSpace: "nowrap", minWidth: 90 }}>Split SC:</span>
            <div style={{ display: "flex", gap: 4, flex: 1 }}>
              {["proportional", "equal"].map(mode => {
                const active = (group.scMode || "proportional") === mode;
                return (
                  <button key={mode} onClick={() => onChange({ ...group, scMode: mode })} style={{
                    flex: 1, padding: "5px 0", borderRadius: 8, border: `1.5px solid ${active ? c.accent : c.border}`,
                    background: active ? c.accentSoft : c.surfaceAlt,
                    color: active ? c.accent : c.textMuted,
                    fontSize: 11, fontWeight: active ? 700 : 500, cursor: "pointer",
                    fontFamily: "'Space Grotesk', sans-serif", transition: "all 0.15s",
                  }}>
                    {mode === "proportional" ? "Proportional" : "Equal"}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {/* Row 5: Grand Total display (full width) */}
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <label style={{ fontSize: 12, color: c.textMuted, fontWeight: 600 }}>Grand Total</label>
          <div style={{ background: c.accentSoft, border: `1.5px solid ${c.borderStrong}`, borderRadius: 10, padding: "9px 12px", fontSize: 14, fontWeight: 700, color: c.accent, height: 38, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span>{fmt(sym, parseFloat(group.grandTotal) || discountedSubtotal)}</span>
            {serviceCharge > 0 && <span style={{ fontSize: 12, fontWeight: 500, color: c.textMuted }}>SC: {fmt(sym, serviceCharge)}</span>}
          </div>
        </div>
        {/* Discount breakdown chip row */}
        {discount > 0 && (
          <div className="highlight-box" style={{ background: c.dangerSoft, borderColor: c.danger + "55" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: c.danger, marginBottom: 6 }}>
              ✦ Discount split {(group.discountMode || "proportional") === "equal" ? "equally among all members" : "proportionally by each member's share of the subtotal"}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {members.map(m => {
                const ms = memberGroupSubtotal(group.items, m.id);
                if (ms === 0) return null;
                const discShare = memberDiscountShare(group, m.id, subtotal, discount);
                return (
                  <div key={m.id} className="chip" style={{ fontSize: 12, background: c.dangerSoft, color: c.danger }}>
                    <Avatar name={m.name} size={16} />{m.name}: −{fmt(sym, discShare)}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {/* SC breakdown chip row */}
        {serviceCharge > 0 && (
          <div className="highlight-box">
            <div style={{ fontSize: 12, fontWeight: 700, color: c.accent, marginBottom: 6 }}>
              ✦ Service charge split {(group.scMode || "proportional") === "equal" ? "equally among all members" : "proportionally by each member's after-discount share"}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {members.map(m => {
                const ms = memberGroupSubtotal(group.items, m.id);
                if (ms === 0) return null;
                const discShare = memberDiscountShare(group, m.id, subtotal, discount);
                const scShare = memberSCShare(group, m.id, discountedSubtotal, serviceCharge, discShare);
                return (
                  <div key={m.id} className="chip" style={{ fontSize: 12 }}>
                    <Avatar name={m.name} size={16} />{m.name}: +{fmt(sym, scShare)}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Settlement helper ──────────────────────────────────────────────────
function computeSettlements(members, groups) {
  const owed = {};
  const paid = {};
  members.forEach(m => { owed[m.id] = 0; paid[m.id] = 0; });

  groups.forEach(group => {
    members.forEach(m => {
      owed[m.id] += memberGroupTotal(group, m.id);
    });
    if (group.payers) {
      Object.entries(group.payers).forEach(([id, amt]) => {
        if (paid[id] !== undefined) paid[id] += parseFloat(amt) || 0;
      });
    }
  });

  const balances = members.map(m => ({
    id: m.id,
    name: m.name,
    owed: owed[m.id] || 0,
    paid: paid[m.id] || 0,
    net: (paid[m.id] || 0) - (owed[m.id] || 0),
  }));

  const pos = balances.filter(b => b.net > 0.005).map(b => ({ ...b })).sort((a, b) => b.net - a.net);
  const neg = balances.filter(b => b.net < -0.005).map(b => ({ ...b })).sort((a, b) => a.net - b.net);
  const txns = [];
  let i = 0, j = 0;
  while (i < pos.length && j < neg.length) {
    const amount = Math.min(pos[i].net, -neg[j].net);
    txns.push({ from: neg[j].name, to: pos[i].name, amount });
    pos[i].net -= amount;
    neg[j].net += amount;
    if (pos[i].net < 0.005) i++;
    if (neg[j].net > -0.005) j++;
  }
  return { balances, txns };
}

// ── Share Image Modal ──────────────────────────────────────────────────
function ShareImageModal({ title, onClose, c, children }) {
  const contentRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function handleSave() {
    if (!contentRef.current) return;
    setSaving(true); setError("");
    try {
      if (!window.html2canvas) {
        await loadScript("https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js");
      }
      const canvas = await window.html2canvas(contentRef.current, {
        scale: 2, useCORS: true, backgroundColor: "#ffffff", logging: false,
      });
      const filename = `splitly-${title.toLowerCase().replace(/\s+/g, "-")}.png`;

      const blob = await new Promise(resolve => canvas.toBlob(resolve, "image/png"));
      if (!blob) throw new Error("no blob");
      const file = new File([blob], filename, { type: "image/png" });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        // iOS / mobile: trigger native Share sheet
        await navigator.share({ files: [file], title });
      } else {
        // Desktop fallback: regular download
        const link = document.createElement("a");
        link.download = filename;
        link.href = URL.createObjectURL(blob);
        link.click();
        URL.revokeObjectURL(link.href);
      }
    } catch (e) {
      if (e?.name !== "AbortError") { // user cancelling the share sheet isn't an error
        setError("Failed to generate image. Please try again.");
      }
    }
    setSaving(false);
  }

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1100, display: "flex", alignItems: "center",
      justifyContent: "center", background: "rgba(0,0,0,0.55)",
      backdropFilter: "blur(6px)", animation: "fadeIn 0.15s ease",
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        width: "100%", maxWidth: 680, margin: "0 20px", background: c.surface,
        border: `1.5px solid ${c.border}`, borderRadius: 16, maxHeight: "90vh",
        display: "flex", flexDirection: "column", overflow: "hidden",
        boxShadow: c.shadowLg,
      }}>
        <div style={{ padding: "16px 20px", borderBottom: `1.5px solid ${c.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}><FiUpload style={{ verticalAlign: "middle", marginRight: 6 }} />{title}</div>
          <button className="btn-danger" onClick={onClose} style={{ fontSize: 16 }}>✕</button>
        </div>
        <div style={{ overflowX: "auto", overflowY: "auto", flex: 1 }}>
          {/* Capture target — fixed width so mobile viewports don't cramp the rendered image */}
          <div ref={contentRef} style={{
            background: "#ffffff", padding: "24px 20px", width: 600,
            fontFamily: "'Space Grotesk', sans-serif", color: "#1e2b1a",
          }}>
            {children}
            {/* <div style={{ marginTop: 20, paddingTop: 12, borderTop: "1.5px solid #e0e0e0", textAlign: "center", fontSize: 11, color: "#888" }}>
              ✦ <strong style={{ color: "#5a8a5e" }}>Splitly</strong>&nbsp;·&nbsp;All calculations are approximate
            </div> */}
            <div style={{ marginTop: 20, paddingTop: 12, borderTop: "1.5px solid #e0e0e0", textAlign: "center", fontSize: 11, color: "#888" }}>
              ✦ <strong style={{ color: "#5a8a5e" }}>okaystephen.github.io/splitly</strong>
            </div>
          </div>
        </div>
        <div style={{ padding: "14px 20px", borderTop: `1.5px solid ${c.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {error ? <span style={{ fontSize: 12, color: c.danger }}>{error}</span> : <span />}
          <div style={{ display: "flex", gap: 10 }}>
            <button className="btn-ghost" onClick={onClose}>Close</button>
            <button className="btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : <><FiSave style={{ verticalAlign: "middle", marginRight: 5 }} />Save as Image</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Summary share content (light-mode only for image) ─────────────────
function SummaryShareContent({ groups, members, sym }) {
  const memberTotals = members.map(m => {
    let total = 0;
    groups.forEach(group => { total += memberGroupTotal(group, m.id); });
    return { member: m, total };
  });
  const grandTotal = memberTotals.reduce((s, r) => s + r.total, 0);

  // Build split-info lines: one per group that has discount or SC
  const splitInfoLines = [];
  groups.forEach(group => {
    const subtotal = group.items.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);
    const discount = Math.min(parseFloat(group.discount) || 0, subtotal);
    const discountedSubtotal = Math.max(0, subtotal - discount);
    const gTotal = parseFloat(group.grandTotal) || 0;
    const sc = gTotal > 0 ? Math.max(0, gTotal - discountedSubtotal) : 0;
    const name = group.name || "Unnamed Group";
    if (discount > 0) {
      const mode = (group.discountMode || "proportional") === "equal" ? "equally" : "proportionally";
      splitInfoLines.push(`${name}: discount split ${mode}`);
    }
    if (sc > 0) {
      const mode = (group.scMode || "proportional") === "equal" ? "equally" : "proportionally";
      splitInfoLines.push(`${name}: service charge split ${mode}`);
    }
  });

  return (
    <div>
      <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 16, color: "#1e2b1a", display: "flex", alignItems: "center", gap: 8 }}><FiCreditCard />Each Person Owes</div>
      <div style={{ border: "1.5px solid #d4dcd0", borderRadius: 12, overflow: "hidden", marginBottom: 16 }}>
        {memberTotals.map(({ member: m, total }) => (
          <div key={m.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 16px", borderBottom: "1px solid #d4dcd0", fontSize: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 28, height: 28, borderRadius: "50%", background: avatarColor(m.id), color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700 }}>
                {m.name.slice(0, 2).toUpperCase()}
              </div>
              <span style={{ fontWeight: 600 }}>{m.name}</span>
            </div>
            <span style={{ fontWeight: 700, color: "#5a8a5e", fontSize: 16 }}>{fmt(sym, total)}</span>
          </div>
        ))}
        <div style={{ padding: "12px 16px", display: "flex", justifyContent: "space-between", background: "#d8ead9" }}>
          <span style={{ fontWeight: 700, fontSize: 15 }}>Grand Total</span>
          <span style={{ fontWeight: 800, fontSize: 20, color: "#5a8a5e" }}>{fmt(sym, grandTotal)}</span>
        </div>
      </div>
      {groups.map(group => {
        const subtotal = group.items.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);
        const discount = Math.min(parseFloat(group.discount) || 0, subtotal);
        const discountedSubtotal = Math.max(0, subtotal - discount);
        const gTotal = parseFloat(group.grandTotal) || 0;
        const sc = gTotal > 0 ? Math.max(0, gTotal - discountedSubtotal) : 0;
        const hasRows = members.some(m => memberGroupSubtotal(group.items, m.id) > 0);
        if (!hasRows) return null;
        return (
          <div key={group.id} style={{ border: "1.5px solid #d4dcd0", borderRadius: 12, overflow: "hidden", marginBottom: 12 }}>
            <div style={{ padding: "12px 16px", borderBottom: "1.5px solid #d4dcd0", display: "flex", justifyContent: "space-between", background: "#eef1ec" }}>
              <span style={{ fontWeight: 700 }}>{group.name || "Unnamed Group"}</span>
              <span style={{ fontWeight: 700, color: "#5a8a5e" }}>{fmt(sym, gTotal || discountedSubtotal)}</span>
            </div>
            {members.map(m => {
              const ms = memberGroupSubtotal(group.items, m.id);
              if (ms === 0) return null;
              const discShare = memberDiscountShare(group, m.id, subtotal, discount);
              const afterDiscount = ms - discShare;
              const scShare = memberSCShare(group, m.id, discountedSubtotal, sc, discShare);
              const memberTotal = afterDiscount + scShare;
              return (
                <div key={m.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 16px", borderBottom: "1px solid #d4dcd0", fontSize: 13 }}>
                  <span>{m.name}
                    {discount > 0 && <span style={{ color: "#c0392b", fontSize: 11 }}> −{fmt(sym, discShare)}</span>}
                    {sc > 0 && <span style={{ color: "#5a7052", fontSize: 11 }}> +{fmt(sym, scShare)} SC</span>}
                  </span>
                  <span style={{ fontWeight: 700, color: "#5a8a5e" }}>{fmt(sym, memberTotal)}</span>
                </div>
              );
            })}
          </div>
        );
      })}
      {splitInfoLines.length > 0 && (
        <div style={{ marginTop: 10, padding: "10px 14px", background: "#f5f7f4", border: "1.5px solid #d4dcd0", borderRadius: 10 }}>
          {splitInfoLines.map((line, i) => (
            <div key={i} style={{ fontSize: 11, color: "#5a7052", lineHeight: 1.7 }}><FiInfo style={{ verticalAlign: "middle", marginRight: 4 }} />{line}</div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Transactions share content (light-mode only for image) ───────────
function TransactionsShareContent({ groups, members, sym }) {
  return (
    <div>
      <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 16, color: "#1e2b1a", display: "flex", alignItems: "center", gap: 8 }}><FiFileText />Transactions</div>
      {groups.length === 0 && (
        <div style={{ color: "#5a7052", fontSize: 13, fontStyle: "italic" }}>No groups recorded.</div>
      )}
      {groups.map((group, gi) => {
        const subtotal = group.items.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);
        const discount = Math.min(parseFloat(group.discount) || 0, subtotal);
        const discountedSubtotal = Math.max(0, subtotal - discount);
        const gTotal = parseFloat(group.grandTotal) || 0;
        const sc = gTotal > 0 ? Math.max(0, gTotal - discountedSubtotal) : 0;
        const displayTotal = gTotal || discountedSubtotal;
        return (
          <div key={group.id} style={{ border: "1.5px solid #d4dcd0", borderRadius: 12, overflow: "hidden", marginBottom: gi < groups.length - 1 ? 14 : 0 }}>
            {/* Group header */}
            <div style={{ padding: "10px 16px", background: "#eef1ec", borderBottom: "1.5px solid #d4dcd0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 700, fontSize: 14 }}>{group.name || "Unnamed Group"}</span>
              <span style={{ fontWeight: 700, color: "#5a8a5e", fontSize: 14 }}>{fmt(sym, displayTotal)}</span>
            </div>
            {/* Items */}
            {group.items.length === 0 ? (
              <div style={{ padding: "10px 16px", fontSize: 12, color: "#888", fontStyle: "italic" }}>No items.</div>
            ) : group.items.map((it, idx) => {
              const amount = parseFloat(it.amount) || 0;
              const splitNames = it.splitWith
                .map(id => members.find(m => m.id === id)?.name)
                .filter(Boolean);
              return (
                <div key={it.id} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "flex-start",
                  padding: "8px 16px", borderBottom: idx < group.items.length - 1 ? "1px solid #e8ede8" : "none",
                  fontSize: 13,
                }}>
                  <div>
                    <div style={{ fontWeight: 500, color: "#1e2b1a" }}>{it.description || <span style={{ color: "#aaa", fontStyle: "italic" }}>No description</span>}</div>
                    {splitNames.length > 0 && (
                      <div style={{ fontSize: 11, color: "#5a7052", marginTop: 2 }}>
                        Split: {splitNames.join(", ")}
                        {splitNames.length > 1 && <span style={{ color: "#888" }}> ({fmt(sym, amount / splitNames.length)} each)</span>}
                      </div>
                    )}
                  </div>
                  <span style={{ fontWeight: 700, color: "#5a8a5e", whiteSpace: "nowrap", marginLeft: 12 }}>{fmt(sym, amount)}</span>
                </div>
              );
            })}
            {/* Group footer: subtotal / discount / SC / total */}
            <div style={{ borderTop: "1.5px solid #d4dcd0", background: "#f5f7f4" }}>
              <div style={{ padding: "7px 16px", display: "flex", justifyContent: "space-between", fontSize: 12, color: "#5a7052" }}>
                <span>Subtotal</span>
                <span style={{ fontWeight: 700 }}>{fmt(sym, subtotal)}</span>
              </div>
              {discount > 0 && (
                <div style={{ padding: "4px 16px", display: "flex", justifyContent: "space-between", fontSize: 12, color: "#c0392b" }}>
                  <span>Discount</span>
                  <span style={{ fontWeight: 700 }}>−{fmt(sym, discount)}</span>
                </div>
              )}
              {sc > 0 && (
                <div style={{ padding: "4px 16px", display: "flex", justifyContent: "space-between", fontSize: 12, color: "#5a7052" }}>
                  <span>Service Charge</span>
                  <span style={{ fontWeight: 700 }}>+{fmt(sym, sc)}</span>
                </div>
              )}
              {(discount > 0 || sc > 0) && (
                <div style={{ padding: "7px 16px", display: "flex", justifyContent: "space-between", fontSize: 13, borderTop: "1px solid #d4dcd0", color: "#1e2b1a" }}>
                  <span style={{ fontWeight: 700 }}>Total</span>
                  <span style={{ fontWeight: 800, color: "#5a8a5e" }}>{fmt(sym, displayTotal)}</span>
                </div>
              )}
            </div>
          </div>
        );
      })}
      {/* Grand total across all groups */}
      {groups.length > 0 && (() => {
        const grand = groups.reduce((s, g) => {
          const sub = g.items.reduce((ss, it) => ss + (parseFloat(it.amount) || 0), 0);
          const disc = Math.min(parseFloat(g.discount) || 0, sub);
          const gt = parseFloat(g.grandTotal) || 0;
          return s + (gt || Math.max(0, sub - disc));
        }, 0);
        return (
          <div style={{ marginTop: 14, padding: "12px 16px", background: "#d8ead9", borderRadius: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontWeight: 700, fontSize: 14 }}>Grand Total</span>
            <span style={{ fontWeight: 800, fontSize: 18, color: "#5a8a5e" }}>{fmt(sym, grand)}</span>
          </div>
        );
      })()}
    </div>
  );
}

// ── Summary Tab ────────────────────────────────────────────────────────
function SummaryTab({ groups, members, sym, c }) {
  const [showShare, setShowShare] = useState(false);

  const memberTotals = members.map(m => {
    let total = 0;
    groups.forEach(group => { total += memberGroupTotal(group, m.id); });
    return { member: m, total };
  });
  const grandTotal = memberTotals.reduce((s, r) => s + r.total, 0);

  if (members.length === 0) {
    return <div className="empty-state">Add members and transactions to see a summary.</div>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="card">
        <div style={{ padding: "14px 18px", borderBottom: `1.5px solid ${c.border}`, fontWeight: 700, fontSize: 15 }}>
          <FiCreditCard style={{ verticalAlign: "middle", marginRight: 6 }} />Each Person Owes
        </div>
        {memberTotals.map(({ member: m, total }) => (
          <div key={m.id} className="summary-row">
            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: 1 }}>
              <Avatar name={m.name} size={32} />
              <span style={{ fontWeight: 600, wordBreak: "break-word", minWidth: 0 }}>{m.name}</span>
            </div>
            <span className="summary-total" style={{ flexShrink: 0 }}>{fmt(sym, total)}</span>
          </div>
        ))}
        <div style={{ padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center", background: c.accentSoft }}>
          <span style={{ fontWeight: 700, fontSize: 15 }}>Grand Total</span>
          <span style={{ fontWeight: 800, fontSize: 20, color: c.accent }}>{fmt(sym, grandTotal)}</span>
        </div>
      </div>

      {groups.map(group => {
        const subtotal = group.items.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);
        const discount = Math.min(parseFloat(group.discount) || 0, subtotal);
        const discountedSubtotal = Math.max(0, subtotal - discount);
        const gTotal = parseFloat(group.grandTotal) || 0;
        const sc = gTotal > 0 ? Math.max(0, gTotal - discountedSubtotal) : 0;
        return (
          <div key={group.id} className="card">
            <div style={{ padding: "14px 18px", borderBottom: `1.5px solid ${c.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 700, fontSize: 15 }}>{group.name || "Unnamed Group"}</span>
              <div style={{ display: "flex", gap: 8 }}>
                {discount > 0 && <span className="tag" style={{ background: c.dangerSoft, color: c.danger }}>−{fmt(sym, discount)}</span>}
                {sc > 0 && <span className="tag">SC: {fmt(sym, sc)}</span>}
                <span className="tag" style={{ background: c.accentSoft, color: c.accent }}>{fmt(sym, gTotal || discountedSubtotal)}</span>
              </div>
            </div>
            {members.map(m => {
              const ms = memberGroupSubtotal(group.items, m.id);
              if (ms === 0) return null;
              const discShare = memberDiscountShare(group, m.id, subtotal, discount);
              const afterDiscount = ms - discShare;
              const scShare = memberSCShare(group, m.id, discountedSubtotal, sc, discShare);
              return (
                <div key={m.id} className="summary-row" style={{ fontSize: 13 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flex: 1 }}>
                    <Avatar name={m.name} size={24} />
                    <span style={{ wordBreak: "break-word", minWidth: 0 }}>{m.name}</span>
                    {discount > 0 && <span style={{ color: c.danger, fontSize: 12, flexShrink: 0 }}>−{fmt(sym, discShare)}</span>}
                    {sc > 0 && <span style={{ color: c.textMuted, fontSize: 12, flexShrink: 0 }}>+{fmt(sym, scShare)} SC</span>}
                  </div>
                  <span style={{ fontWeight: 700, color: c.accent, flexShrink: 0 }}>{fmt(sym, afterDiscount + scShare)}</span>
                </div>
              );
            })}
          </div>
        );
      })}

      {/* Share button */}
      <button className="btn-ghost" onClick={() => setShowShare(true)}
        style={{ width: "100%", padding: "12px", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 4 }}>
        <FiUpload />Share as Image
      </button>

      {showShare && (
        <ShareImageModal title="Summary" onClose={() => setShowShare(false)} c={c}>
          <SummaryShareContent groups={groups} members={members} sym={sym} />
        </ShareImageModal>
      )}
    </div>
  );
}

// ── Payer Group Card ───────────────────────────────────────────────────
function PayerGroupCard({ group, members, sym, c, onUpdatePayers }) {
  const subtotal = group.items.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);
  const grandTotal = parseFloat(group.grandTotal) || 0;
  const discount = Math.min(parseFloat(group.discount) || 0, subtotal);
  const discountedSubtotal = Math.max(0, subtotal - discount);
  const expected = grandTotal || discountedSubtotal;
  const payers = group.payers || {};

  const totalPaid = members.reduce((s, m) => s + (parseFloat(payers[m.id]) || 0), 0);
  const diff = totalPaid - expected;
  const balanced = Math.abs(diff) < 0.01;
  const overPaid = diff > 0.01;

  const updatePayer = (id, val) => {
    onUpdatePayers({ ...payers, [id]: val });
  };

  const setAllPayer = (id) => {
    const next = {};
    members.forEach(m => { next[m.id] = m.id === id ? String(expected) : ""; });
    onUpdatePayers(next);
  };

  return (
    <div className="payer-group-card">
      <div style={{ padding: "13px 18px", background: c.surfaceAlt, borderBottom: `1.5px solid ${c.border}`, display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontWeight: 700, fontSize: 15, wordBreak: "break-word", minWidth: 0, flex: "1 1 120px" }}>{group.name || "Unnamed Group"}</span>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
          <span className="tag" style={{ background: c.accentSoft, color: c.accent }}>{fmt(sym, expected)}</span>
          {expected > 0 && (
            <span style={{ fontSize: 12, fontWeight: 700, color: balanced ? "#4caf50" : overPaid ? "#ff9800" : c.danger }}>
              {balanced ? "✓ Balanced" : overPaid ? `+${fmt(sym, diff)} over` : `${fmt(sym, diff)} short`}
            </span>
          )}
        </div>
      </div>
      <div style={{ padding: "14px 18px", display: "flex", flexDirection: "column", gap: 10 }}>
        {members.length === 0 ? (
          <div style={{ fontSize: 13, color: c.textMuted, fontStyle: "italic" }}>No members added yet.</div>
        ) : members.map(m => (
          <div key={m.id} style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
            <Avatar name={m.name} size={26} />
            <span style={{ fontSize: 14, fontWeight: 600, flex: "1 1 60px", wordBreak: "break-word", minWidth: 0 }}>{m.name}</span>
            <div className="amount-input-row" style={{ flex: "1 1 100px", minWidth: 90, maxWidth: 160 }}>
              <span className="amount-prefix">{sym}</span>
              <input className="splitly-input" type="number" min="0" placeholder="0.00"
                value={payers[m.id] || ""}
                onChange={e => updatePayer(m.id, e.target.value)} />
            </div>
            <button onClick={() => setAllPayer(m.id)} title={`${m.name} paid all`} style={{
              fontSize: 11, padding: "4px 9px", borderRadius: 8, cursor: "pointer",
              border: `1.5px solid ${c.border}`, background: c.surfaceAlt,
              color: c.textMuted, fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0, transition: "all 0.15s",
            }}
              onMouseOver={e => { e.currentTarget.style.borderColor = c.accent; e.currentTarget.style.color = c.accent; }}
              onMouseOut={e => { e.currentTarget.style.borderColor = c.border; e.currentTarget.style.color = c.textMuted; }}>
              Paid all
            </button>
          </div>
        ))}
        {expected > 0 && members.length > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: c.textMuted, paddingTop: 4, borderTop: `1px solid ${c.border}`, marginTop: 2 }}>
            <span>Total entered: <strong style={{ color: c.text }}>{fmt(sym, totalPaid)}</strong></span>
            <span>Expected: <strong style={{ color: c.text }}>{fmt(sym, expected)}</strong></span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Payer share content ────────────────────────────────────────────────
function PayerShareContent({ groups, members, sym }) {
  const { balances, txns } = computeSettlements(members, groups);
  return (
    <div>
      <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 16, color: "#1e2b1a", display: "flex", alignItems: "center", gap: 8 }}><FiDollarSign />Payer Settlements</div>
      <div style={{ border: "1.5px solid #d4dcd0", borderRadius: 12, overflow: "hidden", marginBottom: 16 }}>
        <div style={{ padding: "10px 16px", background: "#eef1ec", borderBottom: "1.5px solid #d4dcd0", fontWeight: 700, fontSize: 13, letterSpacing: "0.05em" }}>Net Balance</div>
        {balances.map(b => (
          <div key={b.name} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 16px", borderBottom: "1px solid #d4dcd0", fontSize: 13 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 24, height: 24, borderRadius: "50%", background: avatarColor(b.name), color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700 }}>
                {b.name.slice(0, 2).toUpperCase()}
              </div>
              <div>
                <div style={{ fontWeight: 600 }}>{b.name}</div>
                <div style={{ fontSize: 11, color: "#5a7052" }}>Paid {fmt(sym, b.paid)} · Owes {fmt(sym, b.owed)}</div>
              </div>
            </div>
            <span style={{ fontWeight: 700, color: b.net > 0.005 ? "#5a8a5e" : b.net < -0.005 ? "#c0392b" : "#5a7052" }}>
              {b.net > 0.005 ? `+${fmt(sym, b.net)}` : b.net < -0.005 ? fmt(sym, b.net) : "Settled ✓"}
            </span>
          </div>
        ))}
      </div>
      {txns.length > 0 && (
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10, color: "#1e2b1a", display: "flex", alignItems: "center", gap: 6 }}><FiRepeat />Settlements</div>
          {txns.map((t, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, background: "#eef1ec", border: "1.5px solid #d4dcd0", borderRadius: 10, padding: "10px 14px", marginBottom: 8, fontSize: 13 }}>
              <div style={{ width: 24, height: 24, borderRadius: "50%", background: avatarColor(t.from), color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700 }}>
                {t.from.slice(0, 2).toUpperCase()}
              </div>
              <span style={{ fontWeight: 600 }}>{t.from}</span>
              <span style={{ color: "#5a7052" }}>pays</span>
              <div style={{ width: 24, height: 24, borderRadius: "50%", background: avatarColor(t.to), color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700 }}>
                {t.to.slice(0, 2).toUpperCase()}
              </div>
              <span style={{ fontWeight: 600 }}>{t.to}</span>
              <span style={{ marginLeft: "auto", fontWeight: 800, fontSize: 16, color: "#5a8a5e" }}>{fmt(sym, t.amount)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Session Share Content (all tabs combined) ──────────────────────────
function SessionShareContent({ groups, members, sym }) {
  const { balances, txns } = computeSettlements(members, groups);
  const memberTotals = members.map(m => {
    let total = 0;
    groups.forEach(group => { total += memberGroupTotal(group, m.id); });
    return { member: m, total };
  });
  const grandTotal = memberTotals.reduce((s, r) => s + r.total, 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {/* ── Branding ── */}
      <div style={{ paddingTop: 12, textAlign: "center", fontSize: 20, color: "#888" }}>
        ✦ <strong style={{ color: "#5a8a5e" }}>Splitly</strong>
      </div>

      <div style={{ borderTop: "2px dashed #d4dcd0", marginTop: 4 }} />

      {/* ── Transactions ── */}
      <div>
        {/* <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 16, color: "#1e2b1a" }}>🧾 Transactions</div> */}
        {groups.length === 0 ? (
          <div style={{ color: "#5a7052", fontSize: 13, fontStyle: "italic" }}>No groups recorded.</div>
        ) : <TransactionsShareContent groups={groups} members={members} sym={sym} />}
      </div>

      <div style={{ borderTop: "2px dashed #d4dcd0", marginTop: 4 }} />

      {/* ── Summary ── */}
      <div>
        <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 16, color: "#1e2b1a", display: "flex", alignItems: "center", gap: 8 }}><FiCreditCard />Each Person Owes</div>
        {members.length === 0 ? (
          <div style={{ color: "#5a7052", fontSize: 13, fontStyle: "italic" }}>No members added.</div>
        ) : (
          <div>
            <div style={{ border: "1.5px solid #d4dcd0", borderRadius: 12, overflow: "hidden", marginBottom: 16 }}>
              {memberTotals.map(({ member: m, total }) => (
                <div key={m.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 16px", borderBottom: "1px solid #d4dcd0", fontSize: 14 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 28, height: 28, borderRadius: "50%", background: avatarColor(m.name), color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700 }}>
                      {m.name.slice(0, 2).toUpperCase()}
                    </div>
                    <span style={{ fontWeight: 600 }}>{m.name}</span>
                  </div>
                  <span style={{ fontWeight: 700, color: "#5a8a5e", fontSize: 16 }}>{fmt(sym, total)}</span>
                </div>
              ))}
              <div style={{ padding: "12px 16px", display: "flex", justifyContent: "space-between", background: "#d8ead9" }}>
                <span style={{ fontWeight: 700, fontSize: 15 }}>Grand Total</span>
                <span style={{ fontWeight: 800, fontSize: 20, color: "#5a8a5e" }}>{fmt(sym, grandTotal)}</span>
              </div>
            </div>
            {groups.map(group => {
              const subtotal = group.items.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);
              const discount = Math.min(parseFloat(group.discount) || 0, subtotal);
              const discountedSubtotal = Math.max(0, subtotal - discount);
              const gTotal = parseFloat(group.grandTotal) || 0;
              const sc = gTotal > 0 ? Math.max(0, gTotal - discountedSubtotal) : 0;
              const hasRows = members.some(m => memberGroupSubtotal(group.items, m.id) > 0);
              if (!hasRows) return null;
              return (
                <div key={group.id} style={{ border: "1.5px solid #d4dcd0", borderRadius: 12, overflow: "hidden", marginBottom: 12 }}>
                  <div style={{ padding: "12px 16px", borderBottom: "1.5px solid #d4dcd0", display: "flex", justifyContent: "space-between", background: "#eef1ec" }}>
                    <span style={{ fontWeight: 700 }}>{group.name || "Unnamed Group"}</span>
                    <span style={{ fontWeight: 700, color: "#5a8a5e" }}>{fmt(sym, gTotal || discountedSubtotal)}</span>
                  </div>
                  {members.map(m => {
                    const ms = memberGroupSubtotal(group.items, m.id);
                    if (ms === 0) return null;
                    const discShare = memberDiscountShare(group, m.id, subtotal, discount);
                    const afterDiscount = ms - discShare;
                    const scShare = memberSCShare(group, m.id, discountedSubtotal, sc, discShare);
                    const memberTotal = afterDiscount + scShare;
                    return (
                      <div key={m.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 16px", borderBottom: "1px solid #d4dcd0", fontSize: 13 }}>
                        <span>{m.name}
                          {discount > 0 && <span style={{ color: "#c0392b", fontSize: 11 }}> −{fmt(sym, discShare)}</span>}
                          {sc > 0 && <span style={{ color: "#5a7052", fontSize: 11 }}> +{fmt(sym, scShare)} SC</span>}
                        </span>
                        <span style={{ fontWeight: 700, color: "#5a8a5e" }}>{fmt(sym, memberTotal)}</span>
                      </div>
                    );
                  })}
                </div>
              );
            })}
            {/* Split-info footer */}
            {(() => {
              const lines = [];
              groups.forEach(group => {
                const subtotal = group.items.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);
                const discount = Math.min(parseFloat(group.discount) || 0, subtotal);
                const discountedSubtotal = Math.max(0, subtotal - discount);
                const gTotal = parseFloat(group.grandTotal) || 0;
                const sc = gTotal > 0 ? Math.max(0, gTotal - discountedSubtotal) : 0;
                const name = group.name || "Unnamed Group";
                if (discount > 0) {
                  const mode = (group.discountMode || "proportional") === "equal" ? "equally" : "proportionally";
                  lines.push(`${name}: discount split ${mode}`);
                }
                if (sc > 0) {
                  const mode = (group.scMode || "proportional") === "equal" ? "equally" : "proportionally";
                  lines.push(`${name}: service charge split ${mode}`);
                }
              });
              if (lines.length === 0) return null;
              return (
                <div style={{ marginTop: 10, padding: "10px 14px", background: "#f5f7f4", border: "1.5px solid #d4dcd0", borderRadius: 10 }}>
                  {lines.map((line, i) => (
                    <div key={i} style={{ fontSize: 11, color: "#5a7052", lineHeight: 1.7 }}><FiInfo style={{ verticalAlign: "middle", marginRight: 4 }} />{line}</div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}
      </div>

      <div style={{ borderTop: "2px dashed #d4dcd0", marginTop: 4 }} />

      {/* ── Payer Settlements ── */}
      <div>
        <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 16, color: "#1e2b1a", display: "flex", alignItems: "center", gap: 8 }}><FiDollarSign />Payer Settlements</div>
        {members.length === 0 || groups.length === 0 ? (
          <div style={{ color: "#5a7052", fontSize: 13, fontStyle: "italic" }}>No payer data available.</div>
        ) : (
          <div>
            <div style={{ border: "1.5px solid #d4dcd0", borderRadius: 12, overflow: "hidden", marginBottom: 16 }}>
              <div style={{ padding: "10px 16px", background: "#eef1ec", borderBottom: "1.5px solid #d4dcd0", fontWeight: 700, fontSize: 13, letterSpacing: "0.05em" }}>Net Balance</div>
              {balances.map(b => (
                <div key={b.name} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 16px", borderBottom: "1px solid #d4dcd0", fontSize: 13 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 24, height: 24, borderRadius: "50%", background: avatarColor(b.name), color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700 }}>
                      {b.name.slice(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600 }}>{b.name}</div>
                      <div style={{ fontSize: 11, color: "#5a7052" }}>Paid {fmt(sym, b.paid)} · Owes {fmt(sym, b.owed)}</div>
                    </div>
                  </div>
                  <span style={{ fontWeight: 700, color: b.net > 0.005 ? "#5a8a5e" : b.net < -0.005 ? "#c0392b" : "#5a7052" }}>
                    {b.net > 0.005 ? `+${fmt(sym, b.net)}` : b.net < -0.005 ? fmt(sym, b.net) : "Settled ✓"}
                  </span>
                </div>
              ))}
            </div>
            {txns.length > 0 && (
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10, color: "#1e2b1a", display: "flex", alignItems: "center", gap: 6 }}><FiRepeat />Settlements</div>
                {txns.map((t, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, background: "#eef1ec", border: "1.5px solid #d4dcd0", borderRadius: 10, padding: "10px 14px", marginBottom: 8, fontSize: 13 }}>
                    <div style={{ width: 24, height: 24, borderRadius: "50%", background: avatarColor(t.from), color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700 }}>
                      {t.from.slice(0, 2).toUpperCase()}
                    </div>
                    <span style={{ fontWeight: 600 }}>{t.from}</span>
                    <span style={{ color: "#5a7052" }}>pays</span>
                    <div style={{ width: 24, height: 24, borderRadius: "50%", background: avatarColor(t.to), color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700 }}>
                      {t.to.slice(0, 2).toUpperCase()}
                    </div>
                    <span style={{ fontWeight: 600 }}>{t.to}</span>
                    <span style={{ marginLeft: "auto", fontWeight: 800, fontSize: 16, color: "#5a8a5e" }}>{fmt(sym, t.amount)}</span>
                  </div>
                ))}
              </div>
            )}
            {txns.length === 0 && balances.length > 0 && (
              <div style={{ textAlign: "center", padding: "12px", color: "#5a8a5e", fontWeight: 700, fontSize: 14 }}>
                ✓ Everyone is settled up!
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Payer Tab ──────────────────────────────────────────────────────────
function PayerTab({ groups, members, sym, c, onUpdateGroupPayers }) {
  const [showShare, setShowShare] = useState(false);
  const { balances, txns } = computeSettlements(members, groups);
  const anyPaid = groups.some(g => g.payers && Object.values(g.payers).some(v => parseFloat(v) > 0));

  if (members.length === 0) {
    return <div className="empty-state">Add members and transactions to use the Payer tab.</div>;
  }
  if (groups.length === 0) {
    return <div className="empty-state">Add group transactions first, then specify who paid.</div>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
      <div style={{ fontSize: 13, color: c.textMuted, marginBottom: 16, lineHeight: 1.6 }}>
        Specify who paid for each group. Use <strong>Paid all</strong> for a single payer or enter individual amounts.
      </div>
      {groups.map(g => (
        <PayerGroupCard
          key={g.id}
          group={g}
          members={members}
          sym={sym}
          c={c}
          onUpdatePayers={payers => onUpdateGroupPayers(g.id, payers)}
        />
      ))}
      {anyPaid && (
        <>
          {/* Net balances */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ padding: "14px 18px", borderBottom: `1.5px solid ${c.border}`, fontWeight: 700, fontSize: 15 }}>
              <FiSliders style={{ verticalAlign: "middle", marginRight: 6 }} />Net Balance
            </div>
            {balances.map(b => (
              <div key={b.name} className="summary-row">
                <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: 1 }}>
                  <Avatar name={b.name} size={32} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, wordBreak: "break-word" }}>{b.name}</div>
                    <div style={{ fontSize: 11, color: c.textMuted }}>
                      Paid {fmt(sym, b.paid)} · Owes {fmt(sym, b.owed)}
                    </div>
                  </div>
                </div>
                <span style={{
                  fontWeight: 700, fontSize: 16, flexShrink: 0,
                  color: b.net > 0.005 ? c.accent : b.net < -0.005 ? c.danger : c.textMuted,
                }}>
                  {b.net > 0.005 ? `+${fmt(sym, b.net)}` : b.net < -0.005 ? fmt(sym, b.net) : "Settled ✓"}
                </span>
              </div>
            ))}
          </div>

          {/* Settlement transactions */}
          {txns.length > 0 ? (
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ padding: "14px 18px", borderBottom: `1.5px solid ${c.border}`, fontWeight: 700, fontSize: 15 }}>
                <FiRepeat style={{ verticalAlign: "middle", marginRight: 6 }} />Settlements
              </div>
              <div style={{ padding: "14px 18px", display: "flex", flexDirection: "column", gap: 10 }}>
                {txns.map((t, i) => (
                  <div key={i} className="settlement-txn">
                    <Avatar name={t.from} size={28} />
                    <span style={{ fontWeight: 600, wordBreak: "break-word", minWidth: 0 }}>{t.from}</span>
                    <span style={{ color: c.textMuted, fontSize: 13, flexShrink: 0 }}>pays</span>
                    <Avatar name={t.to} size={28} />
                    <span style={{ fontWeight: 600, wordBreak: "break-word", minWidth: 0 }}>{t.to}</span>
                    <span style={{ marginLeft: "auto", fontWeight: 800, fontSize: 16, color: c.accent, flexShrink: 0 }}>{fmt(sym, t.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "16px", color: c.accent, fontWeight: 700, fontSize: 14 }}>
              ✓ Everyone is settled up!
            </div>
          )}

          <button className="btn-ghost" onClick={() => setShowShare(true)}
            style={{ width: "100%", padding: "12px", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 4 }}>
            <FiUpload />Share as Image
          </button>
        </>
      )}
      {showShare && (
        <ShareImageModal title="Payer Settlements" onClose={() => setShowShare(false)} c={c}>
          <PayerShareContent groups={groups} members={members} sym={sym} />
        </ShareImageModal>
      )}
    </div>
  );
}

// ── Settled Tab ────────────────────────────────────────────────────────
// Tracks how much each debtor has paid back so far. Status is derived:
// paid >= owed → settled, 0 < paid < owed → partial, otherwise unsettled.
function SettledTab({ groups, members, sym, c, settled, onUpdateSettled }) {
  const { balances } = computeSettlements(members, groups);
  const debtors = balances.filter(b => b.net < -0.005);
  const anyPaid = groups.some(g => g.payers && Object.values(g.payers).some(v => parseFloat(v) > 0));

  if (members.length === 0) {
    return <div className="empty-state">Add members and transactions to track settlements.</div>;
  }
  if (groups.length === 0 || !anyPaid) {
    return <div className="empty-state">Record who paid in the Payer tab first, then track repayments here.</div>;
  }
  if (debtors.length === 0) {
    return <div style={{ textAlign: "center", padding: 16, color: c.accent, fontWeight: 700, fontSize: 14 }}>✓ Everyone is settled up!</div>;
  }

  const rows = debtors.map(b => {
    const owed = -b.net;
    const paid = Math.min(Math.max(parseFloat(settled[b.id]) || 0, 0), owed);
    const status = paid >= owed - 0.005 ? "settled" : paid > 0.005 ? "partial" : "unsettled";
    return { ...b, owed, paid, remaining: Math.max(0, owed - paid), status };
  });
  const totalOwed = rows.reduce((s, r) => s + r.owed, 0);
  const totalPaid = rows.reduce((s, r) => s + r.paid, 0);
  const doneCount = rows.filter(r => r.status === "settled").length;

  const statusStyle = {
    settled: { color: c.accent, bg: c.accentSoft, label: "Settled" },
    partial: { color: "#e08a00", bg: "rgba(224,138,0,0.14)", label: "Partial" },
    unsettled: { color: c.danger, bg: c.dangerSoft, label: "Unsettled" },
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
      <div style={{ fontSize: 13, color: c.textMuted, marginBottom: 16, lineHeight: 1.6 }}>
        Tick <strong>Settled</strong> when someone has paid in full, or enter the amount they've paid so far for partial payments.
      </div>
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ padding: "14px 18px", borderBottom: `1.5px solid ${c.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontWeight: 700, fontSize: 15 }}>
            <FiCheckSquare style={{ verticalAlign: "middle", marginRight: 6 }} />Repayments
          </span>
          <span className="tag" style={{ background: c.accentSoft, color: c.accent }}>{doneCount}/{rows.length} settled</span>
        </div>
        {rows.map(r => {
          const st = statusStyle[r.status];
          return (
            <div key={r.id} style={{ padding: "12px 18px", borderBottom: `1px solid ${c.border}`, display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <input type="checkbox" checked={r.status === "settled"}
                  onChange={e => onUpdateSettled(r.id, e.target.checked ? String(Math.round(r.owed * 100) / 100) : "")}
                  style={{ width: 18, height: 18, accentColor: c.accent, cursor: "pointer", flexShrink: 0 }}
                  aria-label={`${r.name} settled`} />
                <Avatar name={r.name} size={30} />
                <div style={{ flex: "1 1 80px", minWidth: 0 }}>
                  <div style={{ fontWeight: 600, wordBreak: "break-word", textDecoration: r.status === "settled" ? "line-through" : "none", opacity: r.status === "settled" ? 0.6 : 1 }}>{r.name}</div>
                  <div style={{ fontSize: 11, color: c.textMuted }}>Owes {fmt(sym, r.owed)}</div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 20, background: st.bg, color: st.color, flexShrink: 0 }}>{st.label}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", paddingLeft: 28 }}>
                <span style={{ fontSize: 12, color: c.textMuted, fontWeight: 600 }}>Paid so far</span>
                <div className="amount-input-row" style={{ flex: "1 1 100px", minWidth: 90, maxWidth: 160 }}>
                  <span className="amount-prefix">{sym}</span>
                  <input className="splitly-input" type="number" min="0" placeholder="0.00"
                    value={settled[r.id] ?? ""}
                    onChange={e => onUpdateSettled(r.id, e.target.value)} />
                </div>
                <span style={{ fontSize: 12, color: r.remaining > 0.005 ? c.danger : c.accent, fontWeight: 700, marginLeft: "auto" }}>
                  {r.remaining > 0.005 ? `${fmt(sym, r.remaining)} left` : "Paid in full"}
                </span>
              </div>
            </div>
          );
        })}
        <div style={{ padding: "12px 18px", display: "flex", justifyContent: "space-between", fontSize: 13, background: c.surfaceAlt }}>
          <span style={{ color: c.textMuted }}>Collected: <strong style={{ color: c.text }}>{fmt(sym, totalPaid)}</strong> of <strong style={{ color: c.text }}>{fmt(sym, totalOwed)}</strong></span>
          <span style={{ fontWeight: 700, color: c.accent }}>{fmt(sym, Math.max(0, totalOwed - totalPaid))} outstanding</span>
        </div>
      </div>
    </div>
  );
}

// ── Transactions Tab ───────────────────────────────────────────────────
function TransactionsTab({ groups, members, sym, c, addGroup, updateGroup, removeGroup, setTab }) {
  const [showShare, setShowShare] = useState(false);

  return (
    <section>
      {groups.length === 0 && (
        <div className="empty-state card" style={{ padding: "40px 20px" }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}><FiFileText /></div>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>No groups yet</div>
          <div style={{ marginBottom: 16 }}>Create a group for each venue or occasion (e.g. a mano).</div>
          <button className="btn-primary" onClick={addGroup}>+ New Group</button>
        </div>
      )}
      {groups.map(g => (
        <GroupCard key={g.id} group={g} members={members} sym={sym} c={c}
          onChange={next => updateGroup(g.id, next)} onRemove={() => removeGroup(g.id)} />
      ))}
      {groups.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "center" }}>
          <button className="btn-ghost" onClick={addGroup} style={{ width: "100%", padding: "12px" }}>
            + New Group
          </button>
          <button className="btn-ghost" onClick={() => setShowShare(true)}
            style={{ width: "100%", padding: "12px", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            <FiUpload />Share as Image
          </button>
          <button onClick={() => setTab("summary")} style={{
            background: "transparent", border: "none", color: c.accent, fontSize: 13,
            fontWeight: 600, cursor: "pointer", fontFamily: "'Space Grotesk', sans-serif",
            padding: "4px 8px", borderRadius: 8, transition: "opacity 0.15s",
            textDecoration: "underline", opacity: 0.85,
          }}
            onMouseOver={e => e.currentTarget.style.opacity = "1"}
            onMouseOut={e => e.currentTarget.style.opacity = "0.85"}>
            See Summary →
          </button>
        </div>
      )}
      {showShare && (
        <ShareImageModal title="Transactions" onClose={() => setShowShare(false)} c={c}>
          <TransactionsShareContent groups={groups} members={members} sym={sym} />
        </ShareImageModal>
      )}
    </section>
  );
}

// ── Header Bar (with hamburger menu for mobile) ────────────────────────
function Spinner({ size = 20, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 50 50" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
      <circle cx="25" cy="25" r="20" fill="none" stroke={color} strokeWidth="5" strokeLinecap="round" strokeDasharray="85 60">
        <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="0.8s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
}

// ── QR Export/Import Modal ──────────────────────────────────────────────
function QRModal({ mode, c, onClose, onExportRequest, onImportKey, summary }) {
  // mode: "export" | "import"
  const qrHostRef = useRef(null);
  const [key, setKey] = useState(null);
  const [status, setStatus] = useState(mode === "export" ? "loading" : "idle"); // loading | ready | error
  const [manualKey, setManualKey] = useState("");
  const [importing, setImporting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const requestedRef = useRef(false);
  const manualKeyInputRef = useRef(null);

  useEffect(() => {
    if (mode === "import") manualKeyInputRef.current?.focus();
  }, [mode]);

  const [errorText, setErrorText] = useState("");

  const [scanning, setScanning] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [showTapFallback, setShowTapFallback] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const stopScanRef = useRef(null);
  const tapFallbackTimerRef = useRef(null);

  const stopCamera = () => {
    stopScanRef.current?.();
    stopScanRef.current = null;
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    clearTimeout(tapFallbackTimerRef.current);
    setScanning(false);
    setVideoPlaying(false);
    setShowTapFallback(false);
  };

  const [closing, setClosing] = useState(false);
  const closeTimerRef = useRef(null);
  const CLOSE_MS = 200;

  useEffect(() => () => clearTimeout(closeTimerRef.current), []);

  const requestClose = () => {
    if (closing) return;
    stopCamera();
    setClosing(true);
    closeTimerRef.current = setTimeout(onClose, CLOSE_MS);
  };

  const requestCloseRef = useRef(requestClose);
  useEffect(() => { requestCloseRef.current = requestClose; });

  useEffect(() => stopCamera, []); // cleanup on unmount

  const startCamera = async () => {
    setCameraError("");
    setErrorMsg("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
      });
      streamRef.current = stream;
      setScanning(true); // <video> mounts; attaching the stream happens in the effect below
    } catch (err) {
      setCameraError(err.name === "NotAllowedError"
        ? "Camera permission denied."
        : "Could not access camera.");
    }
  };

  // Attach the stream once the <video> element has actually mounted (post-commit),
  // instead of right after setScanning(true) which can race the render.
  const onImportKeyRef = useRef(onImportKey);
  useEffect(() => { onImportKeyRef.current = onImportKey; }, [onImportKey]);

  useEffect(() => {
    if (!scanning || !streamRef.current || !videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    video.muted = true;
    video.setAttribute("muted", "");
    video.setAttribute("webkit-playsinline", "true");
    video.srcObject = streamRef.current;

    const tryPlay = () => video.play().catch(() => { });
    const onPlaying = () => { setVideoPlaying(true); setShowTapFallback(false); clearTimeout(tapFallbackTimerRef.current); };
    video.addEventListener("playing", onPlaying);
    video.addEventListener("canplay", tryPlay);
    tryPlay();

    // Safari can silently drop the very first play() call if too much time
    // passed while waiting on the permission prompt (losing "user gesture"
    // status). If the feed still isn't playing shortly after, surface a
    // button so a fresh tap can restart playback with a real gesture.
    tapFallbackTimerRef.current = setTimeout(() => {
      if (video.paused) setShowTapFallback(true);
    }, 1200);

    stopScanRef.current = scanQRFromVideo(
      video,
      canvasRef.current,
      async (decoded) => {
        stopCamera();
        setImporting(true);
        try {
          await onImportKeyRef.current(decoded.trim());
          requestCloseRef.current();
        } catch (err) {
          setErrorMsg(err.message || "Could not read QR code.");
        } finally {
          setImporting(false);
        }
      },
      () => setCameraError("Could not load the QR scanning library. Check your connection.")
    );
    return () => {
      video.removeEventListener("playing", onPlaying);
      video.removeEventListener("canplay", tryPlay);
      clearTimeout(tapFallbackTimerRef.current);
      stopScanRef.current?.();
      stopScanRef.current = null;
    };
  }, [scanning]);

  useEffect(() => {
    if (mode !== "export" || requestedRef.current) return;
    requestedRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const k = await onExportRequest();
        if (cancelled) return;
        setKey(k);
        setStatus("ready");
      } catch (err) {
        if (!cancelled) {
          setErrorText(err.message || "Could not upload session. Check your connection and try again.");
          setStatus("error");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [mode, onExportRequest]);

  useEffect(() => {
    if (status !== "ready" || !key || !qrHostRef.current || !window.QRCode) return;
    qrHostRef.current.innerHTML = "";
    new window.QRCode(qrHostRef.current, { text: key, width: 200, height: 200 });
  }, [status, key]);

  const handleFilePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    setErrorMsg("");
    try {
      const decoded = await decodeQRImage(file);
      await onImportKey(decoded.trim());
      requestClose();
    } catch (err) {
      setErrorMsg(err.message || "Could not read QR code.");
    } finally {
      setImporting(false);
    }
  };

  const handleManualSubmit = async () => {
    const k = manualKey.trim();
    if (!k) return;
    setImporting(true);
    setErrorMsg("");
    try {
      await onImportKey(k);
      requestClose();
    } catch (err) {
      setErrorMsg(err.message || "Session not found.");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div onClick={requestClose} className={`qr-modal-overlay${closing ? " closing" : ""}`} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 500,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} className={`qr-modal-panel${closing ? " closing" : ""}`} style={{
        background: c.surface, borderRadius: 16, padding: 24, maxWidth: 340, width: '100%',
        boxShadow: c.shadowLg, fontFamily: "'Space Grotesk', sans-serif", color: c.text,
      }}>
        <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 16 }}>
          {mode === "export" ? "Export session" : "Import session"}
        </div>

        {mode === "export" && (
          <>
            {status === "loading" && (
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
                padding: '24px 0', color: c.textMuted,
              }}>
                <Spinner size={22} color={c.textMuted} />
                Uploading session…
              </div>
            )}
            {status === "error" && <div style={{ color: c.danger, fontSize: 13 }}>{errorText}</div>}
            {status === "ready" && (
              <div style={{ textAlign: 'center' }}>
                {summary && (
                  <div style={{
                    textAlign: 'left', background: c.surfaceAlt, border: `1.5px solid ${c.border}`,
                    borderRadius: 10, padding: '10px 12px', marginBottom: 14, fontSize: 12.5, color: c.text,
                  }}>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>{summary.dateTime}</div>
                    <div style={{ color: c.textMuted, marginBottom: 2 }}>
                      <b style={{ color: c.text }}>Members:</b> {summary.memberNames || "—"}
                    </div>
                    <div style={{ color: c.textMuted }}>
                      <b style={{ color: c.text }}>Groups:</b> {summary.groupNames || "—"}
                    </div>
                  </div>
                )}
                <div ref={qrHostRef} style={{ display: 'inline-block', padding: 12, background: '#fff', borderRadius: 12 }} />
                <div style={{ marginTop: 12, fontSize: 13, color: c.textMuted }}>Scan this on another device to import.</div>
                <div style={{ marginTop: 8, fontSize: 12, fontFamily: 'monospace', color: c.textMuted, wordBreak: 'break-all' }}>{key}</div>
              </div>
            )}
          </>
        )}

        {mode === "import" && (
          <>
            {scanning ? (
              <div style={{ marginBottom: 12 }}>
                <div style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', background: '#000', aspectRatio: '4 / 3' }}>
                  <video ref={videoRef} playsInline autoPlay muted style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  <canvas ref={canvasRef} style={{ display: 'none' }} />
                  {!videoPlaying && (
                    <div style={{
                      position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center', gap: 10, color: '#fff',
                    }}>
                      {showTapFallback ? (
                        <button onClick={() => videoRef.current?.play().catch(() => { })} style={{
                          padding: '9px 16px', borderRadius: 10, border: '1.5px solid #fff', background: 'rgba(255,255,255,0.1)',
                          color: '#fff', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600, fontSize: 13, cursor: 'pointer',
                        }}>Tap to start camera preview</button>
                      ) : (
                        <>
                          <Spinner size={26} color="#fff" />
                          <div style={{ fontSize: 12 }}>Starting camera…</div>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <button onClick={stopCamera} style={{
                  width: '100%', marginTop: 8, padding: '9px 14px', borderRadius: 10, border: `1.5px solid ${c.border}`,
                  background: 'none', color: c.textMuted, fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600,
                  fontSize: 13, cursor: 'pointer',
                }}>Stop scanning</button>
              </div>
            ) : (
              <button onClick={startCamera} disabled={importing} style={{
                width: '100%', padding: '10px 14px', borderRadius: 10, border: `1.5px solid ${c.border}`,
                background: c.accent, color: '#fff', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700,
                fontSize: 14, cursor: importing ? 'not-allowed' : 'pointer', marginBottom: 8,
              }}>Scan with camera</button>
            )}
            {cameraError && <div style={{ fontSize: 12, color: c.danger, marginBottom: 8 }}>{cameraError}</div>}

            <input id="qr-import-file" type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFilePick} />
            <button onClick={() => document.getElementById('qr-import-file').click()} disabled={importing} style={{
              width: '100%', padding: '10px 14px', borderRadius: 10, border: `1.5px solid ${c.border}`,
              background: c.surfaceAlt, color: c.text, fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600,
              fontSize: 14, cursor: importing ? 'not-allowed' : 'pointer', marginBottom: 12,
            }}>Upload QR image instead</button>

            <div style={{ fontSize: 12, color: c.textMuted, textAlign: 'center', margin: '4px 0 12px' }}>or enter key manually</div>

            <div style={{ display: 'flex', gap: 8 }}>
              <input ref={manualKeyInputRef} value={manualKey} onChange={e => setManualKey(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") handleManualSubmit(); }}
                placeholder="e.g. aB3xY9kP"
                style={{
                  flex: 1, padding: '9px 12px', borderRadius: 10, border: `1.5px solid ${c.border}`,
                  background: c.surfaceAlt, color: c.text, fontFamily: 'monospace', fontSize: 14,
                }} />
              <button onClick={handleManualSubmit} disabled={importing || !manualKey.trim()} style={{
                padding: '9px 16px', borderRadius: 10, border: 'none', background: c.accent,
                color: '#fff', fontWeight: 700, fontSize: 14, cursor: importing ? 'not-allowed' : 'pointer',
              }}>Go</button>
            </div>

            {importing && (
              <div style={{
                marginTop: 12, fontSize: 13, color: c.textMuted, textAlign: 'center',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}>
                <Spinner size={15} color={c.textMuted} />
                Importing…
              </div>
            )}
            {errorMsg && <div style={{ marginTop: 12, fontSize: 13, color: c.danger }}>{errorMsg}</div>}
          </>
        )}

        <button onClick={requestClose} style={{
          marginTop: 18, width: '100%', padding: '9px', borderRadius: 10, border: `1.5px solid ${c.border}`,
          background: 'none', color: c.textMuted, fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600,
          fontSize: 13, cursor: 'pointer',
        }}>Close</button>
      </div>
    </div>
  );
}

function HeaderBar({ dark, setDark, c, currency, setCurrency, fontSize, setFontSize, exportSession, importSession, clearSession, canClear, onHowTo, onShareSession, canShare, onExportQR, onImportQR }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => { document.removeEventListener('mousedown', handler); document.removeEventListener('touchstart', handler); };
  }, [menuOpen]);

  const menuRow = (icon, label, onClick, danger = false, disabled = false) => (
    <button onClick={() => { if (!disabled) { onClick(); setMenuOpen(false); } }} style={{
      display: 'flex', alignItems: 'center', gap: 12, width: '100%',
      background: 'none', border: 'none', padding: '11px 16px', cursor: disabled ? 'not-allowed' : 'pointer',
      color: danger ? c.danger : c.text, fontSize: 14, fontWeight: 500,
      fontFamily: "'Space Grotesk', sans-serif", opacity: disabled ? 0.38 : 1,
      borderRadius: 8, transition: 'background 0.15s', textAlign: 'left',
    }}
      onMouseOver={e => { if (!disabled) e.currentTarget.style.background = danger ? c.dangerSoft : c.surfaceAlt; }}
      onMouseOut={e => { e.currentTarget.style.background = 'none'; }}
    >
      <span style={{ fontSize: 16, width: 20, textAlign: 'center' }}>{icon}</span>
      {label}
    </button>
  );

  return (
    <header style={{ background: c.surface, borderBottom: `1.5px solid ${c.border}`, boxShadow: c.shadow, position: 'sticky', top: 0, zIndex: 200 }}>
      <div style={{ maxWidth: 820, margin: '0 auto', padding: '0 16px', height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <button onClick={() => setDark(d => !d)} title={dark ? 'Light mode' : 'Dark mode'} style={{
            width: 34, height: 34, borderRadius: 10, background: c.accent, border: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17,
            cursor: 'pointer', color: dark ? '#0e1a0d' : '#fff', transition: 'background 0.2s, transform 0.15s', flexShrink: 0,
          }}
            onMouseOver={e => e.currentTarget.style.transform = 'scale(1.08)'}
            onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
          >✦</button>
          <span style={{ fontWeight: 800, fontSize: 19, letterSpacing: '-0.03em' }}>Splitly</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <select className='splitly-select' value={currency.code}
            onChange={e => setCurrency(CURRENCIES.find(cc => cc.code === e.target.value))}
            style={{ fontSize: 13, padding: '6px 10px', maxWidth: 110 }}>
            {CURRENCIES.map(cur => <option key={cur.code} value={cur.code}>{cur.symbol} {cur.code}</option>)}
          </select>

          <div style={{ position: 'relative' }} ref={menuRef}>
            <button
              onClick={() => setMenuOpen(o => !o)}
              title='Menu'
              style={{
                width: 36, height: 36, borderRadius: 10,
                background: menuOpen ? c.accentSoft : c.surfaceAlt,
                border: `1.5px solid ${menuOpen ? c.accent : c.border}`,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: 4, cursor: 'pointer', transition: 'all 0.2s', flexShrink: 0,
              }}
            >
              {[0, 1, 2].map(i => (
                <span key={i} style={{
                  display: 'block', width: 16, height: 2, borderRadius: 2,
                  background: menuOpen ? c.accent : c.textMuted,
                  transition: 'all 0.2s',
                  transform: menuOpen
                    ? i === 0 ? 'translateY(6px) rotate(45deg)'
                      : i === 2 ? 'translateY(-6px) rotate(-45deg)'
                        : 'scaleX(0)'
                    : 'none',
                }} />
              ))}
            </button>

            {menuOpen && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 8px)', right: 0,
                background: c.surface, border: `1.5px solid ${c.border}`,
                borderRadius: 14, boxShadow: c.shadowLg, minWidth: 220,
                zIndex: 300, overflow: 'hidden', animation: 'fadeSlide 0.18s ease both',
              }}>
                <div style={{ padding: '10px 16px 6px', borderBottom: `1px solid ${c.border}` }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: c.textMuted, marginBottom: 8 }}>Text Size</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button onClick={() => setFontSize(s => Math.max(10, s - 1))} disabled={fontSize <= 10} style={{
                      flex: 1, padding: '6px', borderRadius: 8, border: `1.5px solid ${c.border}`,
                      background: c.surfaceAlt, color: fontSize <= 10 ? c.border : c.textMuted,
                      fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: 13,
                      cursor: fontSize <= 10 ? 'not-allowed' : 'pointer',
                    }}>A−</button>
                    <span style={{ minWidth: 34, textAlign: 'center', fontSize: 13, fontWeight: 700, color: c.accent }}>{fontSize}px</span>
                    <button onClick={() => setFontSize(s => Math.min(20, s + 1))} disabled={fontSize >= 20} style={{
                      flex: 1, padding: '6px', borderRadius: 8, border: `1.5px solid ${c.border}`,
                      background: c.surfaceAlt, color: fontSize >= 20 ? c.border : c.textMuted,
                      fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: 13,
                      cursor: fontSize >= 20 ? 'not-allowed' : 'pointer',
                    }}>A+</button>
                  </div>
                </div>
                <div style={{ padding: '6px' }}>
                  {menuRow(<FiHelpCircle />, 'How to use', onHowTo)}
                  {menuRow(<FiShare2 />, 'Share session', onShareSession, false, !canShare)}
                  {/* Commented out in favor of QR-based import/export. Uncomment if DB access is ever terminated.
                  {menuRow(<FiUpload />, 'Import session', () => document.getElementById('splitly-import-input').click())}
                  {menuRow(<FiDownload />, 'Export session', exportSession)}
                  */}
                  {menuRow(<FiUpload />, 'Import session', onImportQR)}
                  {menuRow(<FiDownload />, 'Export session', onExportQR, false, !canShare)}
                  <div style={{ height: 1, background: c.border, margin: '4px 8px' }} />
                  {menuRow(<FiTrash2 />, 'Clear session', clearSession, true, !canClear)}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <input id='splitly-import-input' type='file' accept='.json,application/json' style={{ display: 'none' }} onChange={importSession} />
    </header>
  );
}


// ── Main App ───────────────────────────────────────────────────────────
// eslint-disable-next-line import/no-unused-modules
export default function Splitly() {
  const isNightTime = () => { const h = new Date().getHours(); return h >= 20 || h < 7; };
  const [dark, setDark] = useState(() => {
    const saved = localStorage.getItem("splitly-dark");
    return saved !== null ? saved === "true" : isNightTime();
  });
  const [currency, setCurrency] = useState(CURRENCIES[0]);
  const [members, setMembers] = useState([]);
  const [newMember, setNewMember] = useState("");
  const [groups, setGroups] = useState([]);
  const [settled, setSettled] = useState({}); // { [memberId]: amount paid back so far }
  const [tab, setTab] = useState("transactions");
  const [showHowTo, setShowHowTo] = useState(false);
  const [showShareSession, setShowShareSession] = useState(false);
  const [fontSize, setFontSize] = useState(12); // base font size in px
  const [splash, setSplash] = useState(true);
  const [splashFading, setSplashFading] = useState(false);

  useEffect(() => {
    const fadeTimer = setTimeout(() => setSplashFading(true), 900);
    const hideTimer = setTimeout(() => setSplash(false), 1300);
    return () => { clearTimeout(fadeTimer); clearTimeout(hideTimer); };
  }, []);

  const c = dark ? palette.dark : palette.light;
  injectStyles(c, fontSize);
  const sym = currency.symbol;

  // ── Session Import/Export ──
  const exportSession = () => {
    const data = { v: 2, currency: currency.code, members, groups, settled };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `splitly-session-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Shared by file-based and QR/cloud-based import
  const applySessionData = (data) => {
    if (!data.v || !Array.isArray(data.members) || !Array.isArray(data.groups))
      throw new Error("Invalid Splitly session data.");
    const cur = CURRENCIES.find(cc => cc.code === data.currency) || CURRENCIES[0];
    setCurrency(cur);

    // Migrate v1 sessions (name-keyed) to v2 (id-keyed)
    let members = data.members;
    let groups = data.groups;
    if (data.v === 1) {
      // Ensure all members have ids
      members = members.map(m => typeof m === "string" ? { id: uid(), name: m } : (m.id ? m : { ...m, id: uid() }));
      const nameToId = Object.fromEntries(members.map(m => [m.name, m.id]));
      groups = groups.map(g => ({
        ...g,
        items: (g.items || []).map(it => ({
          ...it,
          splitWith: (it.splitWith || []).map(n => nameToId[n] ?? n),
        })),
        payers: Object.fromEntries(
          Object.entries(g.payers || {}).map(([k, v]) => [nameToId[k] ?? k, v])
        ),
      }));
    }

    setMembers(members);
    setGroups(groups.map(g => ({ discountMode: "proportional", scMode: "proportional", payers: {}, notes: "", ...g })));
    setSettled(data.settled && typeof data.settled === "object" ? data.settled : {});
    setTab("transactions");
  };

  const importSession = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        applySessionData(JSON.parse(ev.target.result));
      } catch {
        alert("Could not read the file. Make sure it's a valid Splitly JSON export.");
      }
    };
    reader.readAsText(file);
    e.target.value = ""; // reset so same file can be re-imported
  };

  // ── QR Export/Import (via Vercel + MongoDB) ──
  const [qrModal, setQrModal] = useState(null); // "export" | "import" | null
  const [cloudKey, setCloudKey] = useState(null);

  const exportSessionToCloud = async () => {
    if (members.length === 0 && groups.length === 0) {
      throw new Error("Add members or a group before exporting.");
    }
    const data = { v: 2, currency: currency.code, members, groups, settled };
    const key = await storeSessionCloud(data, cloudKey);
    setCloudKey(key);
    return key;
  };

  const importSessionFromCloud = async (key) => {
    const data = await fetchSessionCloud(key);
    applySessionData(data);
    setCloudKey(key);
  };

  // ── Clear Session ──
  const clearSession = () => {
    if (!window.confirm("Clear entire session? All members and transactions will be deleted.")) return;
    setMembers([]);
    setGroups([]);
    setSettled({});
    setCloudKey(null);
    setTab("transactions");
  };

  const addMember = () => {
    const name = newMember.trim();
    if (!name || members.find(m => m.name.toLowerCase() === name.toLowerCase())) return;
    setMembers(prev => [...prev, { id: uid(), name }]);
    setNewMember("");
  };

  const removeMember = (id) => {
    setMembers(prev => prev.filter(m => m.id !== id));
    setSettled(prev => Object.fromEntries(Object.entries(prev).filter(([k]) => k !== id)));
    setGroups(prev => prev.map(g => ({
      ...g,
      items: g.items.map(it => ({ ...it, splitWith: it.splitWith.filter(sid => sid !== id) })),
      payers: Object.fromEntries(Object.entries(g.payers || {}).filter(([k]) => k !== id)),
    })));
  };

  const addGroup = () => {
    setGroups(prev => [...prev, { id: uid(), name: "", grandTotal: "", discount: "", discountMode: "proportional", scMode: "proportional", items: [], payers: {}, notes: "" }]);
  };

  const updateGroup = (id, next) => setGroups(prev => prev.map(g => g.id === id ? next : g));
  const removeGroup = (id) => setGroups(prev => prev.filter(g => g.id !== id));

  const updateSettled = (memberId, amount) => setSettled(prev => ({ ...prev, [memberId]: amount }));

  const updateGroupPayers = (groupId, payers) => {
    setGroups(prev => prev.map(g => g.id === groupId ? { ...g, payers } : g));
  };

  return (
    <>
      {splash && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 9999,
          background: dark ? palette.dark.bg : palette.light.bg,
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center", gap: 16,
          transition: "opacity 0.4s ease",
          opacity: splashFading ? 0 : 1,
          pointerEvents: splashFading ? "none" : "all",
        }}>
          <div style={{
            width: 72, height: 72, borderRadius: 20,
            background: dark ? palette.dark.accent : palette.light.accent,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 36, boxShadow: dark ? palette.dark.shadowLg : palette.light.shadowLg,
          }}>
            <span className="splash-icon-text">✦</span>
          </div>
          <div style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 700, fontSize: 28, letterSpacing: "-0.03em",
            color: dark ? palette.dark.text : palette.light.text,
          }}>
            Splitly
          </div>
          <div style={{
            fontSize: 13, fontWeight: 500,
            color: dark ? palette.dark.textMuted : palette.light.textMuted,
            letterSpacing: "0.04em",
          }}>
            split bills, not friendships
          </div>
        </div>
      )}
      <div style={{ minHeight: "100vh", background: c.bg, color: c.text, fontFamily: "'Space Grotesk', sans-serif", transition: "background 0.4s ease, color 0.4s ease" }}>
        {/* ── Header ── */}
        <HeaderBar
          dark={dark} setDark={v => { const next = typeof v === "function" ? v(dark) : v; localStorage.setItem("splitly-dark", next); setDark(next); }} c={c}
          currency={currency} setCurrency={setCurrency}
          fontSize={fontSize} setFontSize={setFontSize}
          exportSession={exportSession} importSession={importSession}
          clearSession={clearSession}
          canClear={members.length > 0 || groups.length > 0}
          onHowTo={() => setShowHowTo(true)}
          onShareSession={() => setShowShareSession(true)}
          onExportQR={() => setQrModal("export")}
          onImportQR={() => setQrModal("import")}
          canShare={members.length > 0 || groups.length > 0}
        />

        {qrModal && (
          <QRModal
            mode={qrModal}
            c={c}
            onClose={() => setQrModal(null)}
            onExportRequest={exportSessionToCloud}
            onImportKey={importSessionFromCloud}
            summary={{
              dateTime: new Date().toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }),
              memberNames: members.map(m => m.name).join(", "),
              groupNames: groups.map(g => g.name).join(", "),
            }}
          />
        )}

        <main style={{ maxWidth: 820, margin: "0 auto", padding: "24px 20px 60px" }}>
          {/* ── Members ── */}
          <section style={{ marginBottom: 28 }}>
            <div className="card">
              <div style={{ padding: "16px 20px", borderBottom: `1.5px solid ${c.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontWeight: 700, fontSize: 16, display: "flex", alignItems: "center", gap: 7 }}><FiUsers />Members</div>
                <span className="tag">{members.length} {members.length === 1 ? "person" : "people"}</span>
              </div>
              <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
                {/* Add member input */}
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    className="splitly-input"
                    placeholder="Add a member..."
                    value={newMember}
                    onChange={e => setNewMember(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && addMember()}
                  />
                  <button className="btn-primary" onClick={addMember}
                    disabled={!newMember.trim()} style={{ whiteSpace: "nowrap" }}>Add</button>
                </div>
                {/* Member list */}
                {members.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {members.map(m => (
                      <div key={m.id} className="member-pill fade-slide">
                        <Avatar name={m.name} size={26} />
                        <span>{m.name}</span>
                        <button className="btn-danger" style={{ padding: "2px 5px", fontSize: 12 }} onClick={() => removeMember(m.id)}>✕</button>
                      </div>
                    ))}
                  </div>
                )}
                {members.length === 0 && (
                  <div className="empty-state" style={{ padding: "8px 0" }}>No members yet. Start by adding someone above.</div>
                )}
              </div>
            </div>
          </section>

          {/* ── Tabs ── */}
          <div style={{ display: "flex", gap: 4, marginBottom: 20, background: c.surface, borderRadius: 12, padding: 4, border: `1.5px solid ${c.border}`, width: "fit-content", margin: "0 auto 20px" }}>
            <button className={`tab-btn ${tab === "transactions" ? "active" : ""}`} onClick={() => setTab("transactions")}>Transactions</button>
            <button className={`tab-btn ${tab === "summary" ? "active" : ""}`} onClick={() => setTab("summary")}>Summary</button>
            <button className={`tab-btn ${tab === "payer" ? "active" : ""}`} onClick={() => setTab("payer")}>Payer</button>
            <button className={`tab-btn ${tab === "settled" ? "active" : ""}`} onClick={() => setTab("settled")}>Settled</button>
          </div>

          {/* ── Tab Content (key forces remount = fresh animation on switch) ── */}
          <div key={tab} className="tab-panel">
            {tab === "transactions" && (
              <TransactionsTab
                groups={groups} members={members} sym={sym} c={c}
                addGroup={addGroup} updateGroup={updateGroup} removeGroup={removeGroup}
                setTab={setTab}
              />
            )}

            {tab === "summary" && (
              <SummaryTab groups={groups} members={members} sym={sym} c={c} />
            )}

            {tab === "payer" && (
              <PayerTab groups={groups} members={members} sym={sym} c={c} onUpdateGroupPayers={updateGroupPayers} />
            )}

            {tab === "settled" && (
              <SettledTab groups={groups} members={members} sym={sym} c={c} settled={settled} onUpdateSettled={updateSettled} />
            )}
          </div>
        </main>

        {/* ── Footer ── */}
        <footer style={{ textAlign: "center", padding: "24px 20px 32px", borderTop: `1.5px solid ${c.border}`, color: c.textMuted, fontSize: 12, lineHeight: 1.6 }}>
          <div style={{ marginBottom: 4 }}>✦ <strong style={{ color: c.accent }}>Splitly</strong></div>
          <div style={{ opacity: 0.7 }}>Your session lives only in this browser tab and isn't saved automatically.<br />QR export stores a copy only when you choose to export.</div>
        </footer>

        {showHowTo && <HowToModal onClose={() => setShowHowTo(false)} c={c} />}

        {showShareSession && (
          <ShareImageModal title="Share Session" onClose={() => setShowShareSession(false)} c={c}>
            <SessionShareContent groups={groups} members={members} sym={sym} />
          </ShareImageModal>
        )}

      </div>
    </>
  );
}